/**
 * @page TelefoniePage
 * @description #1430 — Telefonie-Modul UI-Dach: 5 Unterseiten (Dashboard, Anruf-Verlauf,
 * Aufnahmen-Archiv, Anruf-Regeln, Einstellungen). Einstellungen = read-only VOIP-Feature-Status
 * (Kill-Switches, Twilio-Nummer, Webhook-Ziel) — KEINE Secrets.
 * #1431 — Reporting-UI: Anruf-Verlauf (Tabelle + Filter + Pagination + Event-Drilldown + CSV)
 * + Dashboard (KPIs, Volumen-Chart Recharts, Stunden-Heatmap, Ergebnis/MA/Intents).
 * #1436 — Aufnahmen-Archiv: Liste + Filter + Transkript-Volltextsuche + Audio-Player
 * (<audio> gegen Range-Stream-Endpoint) + Transkript mit Sprecher-Trennung + DSGVO-Löschen.
 * @route /telefonie, /telefonie/verlauf, /telefonie/aufnahmen, /telefonie/regeln, /telefonie/einstellungen
 * @roles modul 'telefonie' (default: administrator, ops_manager); Löschen = Permission FULL
 * @i18n telefonie.*
 * @endpoints GET /api/voip/settings-status, /api/voip/calls, /api/voip/calls/:id/events, /api/voip/calls/export, /api/voip/stats, /api/voip/recordings[...], DELETE /api/voip/recordings/:id
 * @since v2.668.0 (#1430), v2.669.0 (#1431), v2.670.0 (#1436)
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Phone, LayoutDashboard, History, Mic, GitBranch, Settings,
  CheckCircle2, MoonStar, Loader2, AlertTriangle, KeyRound, Webhook,
  PhoneIncoming, PhoneOutgoing, Download, ChevronLeft, ChevronRight,
  ChevronDown, ChevronUp, ExternalLink, Search, Clock, Headphones,
  PhoneMissed, Bot, ArrowRightLeft, Users, Lightbulb, Trash2, Hash, Ban, Check, Zap,
  Sparkles, Copy, ClipboardCheck, Gauge, Play, Volume2, CalendarClock, Plus,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TelefonieRegelnBuilder from './TelefonieRegelnBuilder';

const API = import.meta.env.VITE_API_URL || '';

function PageHead({ title, subtitle }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-[var(--text-primary)]">{title}</h1>
      <p className="text-sm text-[var(--text-secondary)] mt-1">{subtitle}</p>
    </div>
  );
}

function Placeholder({ Icon, titleKey, descKey, ticket }) {
  const { t } = useLanguage();
  return (
    <div className="p-6 space-y-6">
      <PageHead title={t(titleKey)} subtitle={t('telefonie.subtitle')} />
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-center mb-4">
            <Icon size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="text-base font-semibold text-[var(--text-primary)] mb-1">{t('telefonie.placeholder.title')}</h3>
          <p className="text-sm text-[var(--text-tertiary)] max-w-sm">{t(descKey)}</p>
          {ticket && <p className="text-xs text-[var(--text-muted)] mt-3">{ticket}</p>}
        </div>
      </div>
    </div>
  );
}

// ── #1431 Gemeinsame Reporting-Helfer ──

const ERGEBNISSE = ['ki_geloest', 'case_angelegt', 'durchgestellt', 'voicemail', 'verpasst', 'blockiert', 'abgebrochen'];

const ERGEBNIS_BADGE = {
  ki_geloest: 'bg-green-100 text-green-700',
  case_angelegt: 'bg-blue-100 text-blue-700',
  durchgestellt: 'bg-indigo-100 text-indigo-700',
  voicemail: 'bg-amber-100 text-amber-700',
  verpasst: 'bg-red-100 text-red-700',
  blockiert: 'bg-gray-200 text-gray-600',
  abgebrochen: 'bg-gray-100 text-gray-500',
  offen: 'bg-gray-100 text-gray-500',
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-AT', {
    timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function fmtDauer(sek) {
  if (sek === null || sek === undefined) return '—';
  const s = Number(sek);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')} min`;
}

function ErgebnisBadge({ ergebnis }) {
  const { t } = useLanguage();
  const key = ergebnis || 'offen';
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${ERGEBNIS_BADGE[key] || ERGEBNIS_BADGE.offen}`}>
      {t(`telefonie.ergebnis.${key}`)}
    </span>
  );
}

// #1477 — verwendeter Sprach-Provider/Modell pro Anruf (cascade=Claude /
// s2s_openai=OpenAI Realtime). NULL = kein KI-Anruf (Softphone/Outbound/intern).
const PROVIDER_BADGE = {
  cascade: 'bg-indigo-100 text-indigo-700',
  s2s_openai: 'bg-cyan-100 text-cyan-700',
};
function ProviderBadge({ provider, model }) {
  const { t } = useLanguage();
  if (!provider) return <span className="text-[var(--text-muted)]">—</span>;
  const label = t(`telefonie.provider.${provider}`);
  return (
    <span
      title={model || ''}
      className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${PROVIDER_BADGE[provider] || 'bg-gray-100 text-gray-500'}`}
    >
      {label && label !== `telefonie.provider.${provider}` ? label : provider}
    </span>
  );
}

const inputCls = 'h-9 px-3 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30';

function buildQuery(filters, extra = {}) {
  const p = new URLSearchParams();
  Object.entries({ ...filters, ...extra }).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') p.set(k, v); });
  return p.toString();
}

// ── #1431 Anruf-Verlauf: Tabelle + Filter + Pagination + Drilldown + CSV ──

function EventTimeline({ callId }) {
  const { t } = useLanguage();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/voip/calls/${callId}/events`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => setEvents(d.events || []))
      .catch((e) => setError(e.message));
  }, [callId]);

  if (error) return <p className="text-sm text-red-600 py-3">{t('telefonie.verlauf.eventsError')}: {error}</p>;
  if (!events) return <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-3"><Loader2 size={14} className="animate-spin" /> {t('common.loading')}</div>;
  if (!events.length) return <p className="text-sm text-[var(--text-muted)] py-3">{t('telefonie.verlauf.noEvents')}</p>;

  return (
    <div className="py-3 pl-2">
      <ol className="relative border-l-2 border-[var(--border-card,#E5E7EB)] space-y-3 ml-2">
        {events.map((ev) => (
          <li key={ev.id} className="ml-4">
            <span className="absolute -left-[5px] mt-1.5 w-2 h-2 rounded-full bg-[#338edc]" />
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="text-xs font-mono text-[var(--text-tertiary)]">{fmtDateTime(ev.at)}</span>
              <span className="text-sm font-medium text-[var(--text-primary)]">{t(`telefonie.event.${ev.event_typ}`, ev.event_typ)}</span>
              {ev.stufe && <span className="text-xs text-[var(--text-secondary)]">{t('telefonie.verlauf.stufe')}: {t(`telefonie.stufe.${ev.stufe}`, ev.stufe)}</span>}
              {ev.ziel && <span className="text-xs text-[var(--text-secondary)]">{t('telefonie.verlauf.ziel')}: {ev.ziel}</span>}
            </div>
            {ev.detail && Object.keys(ev.detail).length > 0 && (
              <code className="block text-[11px] text-[var(--text-muted)] mt-0.5 break-all">{JSON.stringify(ev.detail)}</code>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** #1443 — „Nummer blockieren" aus dem Verlauf (2-Klick-Confirm; 409 = bereits drauf → done). */
function BlockNumberButton({ nummer }) {
  const { t } = useLanguage();
  const [state, setState] = useState('idle'); // idle | confirm | busy | done | error

  if (!nummer || !/^\+\d{6,15}$/.test(nummer)) return null;
  if (state === 'done') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-600">
        <Ban size={11} /> {t('telefonie.blocklist.blocked')}
      </span>
    );
  }

  const doBlock = async (e) => {
    e.stopPropagation();
    if (state === 'idle') { setState('confirm'); return; }
    setState('busy');
    try {
      const r = await fetch(`${API}/api/voip/blocklist`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nummer, grund: t('telefonie.blocklist.grundVerlauf') }),
      });
      if (r.ok || r.status === 409) setState('done');
      else setState('error');
    } catch { setState('error'); }
  };

  return (
    <button
      onClick={doBlock}
      disabled={state === 'busy'}
      title={t('telefonie.blocklist.blockFromCall')}
      className={`inline-flex items-center gap-1 h-6 px-2 text-xs font-medium rounded-md border transition-colors ${
        state === 'confirm'
          ? 'border-red-300 bg-red-50 text-red-700'
          : state === 'error'
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-tertiary)] hover:text-red-600'
      }`}
    >
      {state === 'busy' ? <Loader2 size={11} className="animate-spin" /> : <Ban size={11} />}
      {state === 'confirm' ? t('telefonie.blocklist.blockConfirm') : state === 'error' ? t('common.error') : ''}
    </button>
  );
}

export function TelefonieVerlaufPage() {
  const { t } = useLanguage();
  // #1806 Restbefund Anruf 2 — Deep-Link aus Fall-Aktivitäten: ?call=<voip_calls.id>
  const [searchParams, setSearchParams] = useSearchParams();
  const callIdParam = Number(searchParams.get('call')) > 0 ? Number(searchParams.get('call')) : null;
  const [filters, setFilters] = useState({ von: '', bis: '', richtung: '', ergebnis: '', mitarbeiter_id: '' });
  const [nummerInput, setNummerInput] = useState('');
  const [nummer, setNummer] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [maList, setMaList] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const tmr = setTimeout(() => { setNummer(nummerInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(tmr);
  }, [nummerInput]);

  useEffect(() => {
    fetch(`${API}/api/voip/stats`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMaList(d?.pro_mitarbeiter || []))
      .catch(() => {});
  }, []);

  const query = buildQuery({ ...filters, nummer, call_id: callIdParam || '' }, { page, page_size: 50 });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API}/api/voip/calls?${query}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [query]);

  // Deep-Link: den verlinkten Anruf automatisch aufklappen (Events + Aufnahme sichtbar).
  useEffect(() => {
    if (callIdParam && data?.calls?.some((c) => c.id === callIdParam)) setExpanded(callIdParam);
  }, [callIdParam, data]);

  const setFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
    setExpanded(null);
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/voip/calls/export?${buildQuery({ ...filters, nummer })}`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `anruf-verlauf-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHead title={t('telefonie.verlauf.title')} subtitle={t('telefonie.verlauf.subtitle')} />
        <button
          onClick={exportCsv}
          disabled={exporting}
          className="inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] text-[var(--text-primary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors disabled:opacity-50"
        >
          {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          {t('telefonie.verlauf.exportCsv')}
        </button>
      </div>

      {/* Filter-Leiste */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterVon')}</label>
          <input type="date" value={filters.von} onChange={(e) => setFilter('von', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterBis')}</label>
          <input type="date" value={filters.bis} onChange={(e) => setFilter('bis', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterRichtung')}</label>
          <select value={filters.richtung} onChange={(e) => setFilter('richtung', e.target.value)} className={inputCls}>
            <option value="">{t('telefonie.verlauf.alle')}</option>
            <option value="inbound">{t('telefonie.richtung.inbound')}</option>
            <option value="outbound">{t('telefonie.richtung.outbound')}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterErgebnis')}</label>
          <select value={filters.ergebnis} onChange={(e) => setFilter('ergebnis', e.target.value)} className={inputCls}>
            <option value="">{t('telefonie.verlauf.alle')}</option>
            {ERGEBNISSE.map((erg) => <option key={erg} value={erg}>{t(`telefonie.ergebnis.${erg}`)}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterMa')}</label>
          <select value={filters.mitarbeiter_id} onChange={(e) => setFilter('mitarbeiter_id', e.target.value)} className={inputCls}>
            <option value="">{t('telefonie.verlauf.alle')}</option>
            {maList.map((ma) => <option key={ma.mitarbeiter_id} value={ma.mitarbeiter_id}>{ma.mitarbeiter_name}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterNummer')}</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              value={nummerInput}
              onChange={(e) => setNummerInput(e.target.value)}
              placeholder={t('telefonie.verlauf.nummerPlaceholder')}
              className={`${inputCls} w-full pl-8`}
            />
          </div>
        </div>
      </div>

      {callIdParam && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-primary)]">
            <Phone size={13} className="text-[#338edc]" />
            {t('telefonie.verlauf.filterCall')} #{callIdParam}
            <button
              onClick={() => { setSearchParams({}); setPage(1); setExpanded(null); }}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)] font-bold leading-none"
              title={t('telefonie.verlauf.filterCallClear')}
            >×</button>
          </span>
        </div>
      )}

      {error && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">{t('telefonie.verlauf.loadError')}: {error}</span>
          </div>
        </div>
      )}

      {/* Tabelle */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                {['colZeit', 'colRichtung', 'colNummer', 'colDauer', 'colErgebnis', 'colProvider', 'colMa', 'colCase', 'colAufnahme', ''].map((key, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] whitespace-nowrap">
                    {key ? t(`telefonie.verlauf.${key}`) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
              {loading && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                  <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> {t('common.loading')}</span>
                </td></tr>
              )}
              {!loading && data && data.calls.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[var(--text-muted)]">{t('telefonie.verlauf.empty')}</td></tr>
              )}
              {!loading && data && data.calls.map((c) => (
                <React.Fragment key={c.id}>
                  <tr
                    className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors cursor-pointer"
                    onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-primary)]">{fmtDateTime(c.gestartet_at)}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[var(--text-secondary)]">
                        {c.richtung === 'inbound'
                          ? <PhoneIncoming size={14} className="text-[#338edc]" />
                          : <PhoneOutgoing size={14} className="text-[#6366F1]" />}
                        {t(`telefonie.richtung.${c.richtung}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-[var(--text-primary)]">
                      <span className="inline-flex items-center gap-2">
                        {c.richtung === 'inbound' ? (c.von_anzeige || c.von_nummer) : (c.an_anzeige || c.an_nummer)}
                        {c.richtung === 'inbound' && <BlockNumberButton nummer={c.von_nummer} />}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-primary)]">{fmtDauer(c.dauer_sek)}</td>
                    <td className="px-4 py-3"><ErgebnisBadge ergebnis={c.ergebnis} /></td>
                    <td className="px-4 py-3"><ProviderBadge provider={c.voice_provider} model={c.voice_model} /></td>
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-secondary)]">{c.mitarbeiter_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {c.case_id ? (
                        <Link
                          to={`/cases/${c.case_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-[#338edc] hover:underline"
                        >
                          #{c.case_id} <ExternalLink size={12} />
                        </Link>
                      ) : <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {c.aufnahme_id
                        ? <span title={t('telefonie.verlauf.aufnahmeVorhanden')}><Mic size={15} className="text-[#338edc]" /></span>
                        : <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">
                      {expanded === c.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </td>
                  </tr>
                  {expanded === c.id && (
                    <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                      <td colSpan={10} className="px-6">
                        {c.meta?.ttfa?.avg_ms != null && (
                          <div className="flex items-center gap-2 pt-3 text-xs text-[var(--text-tertiary)]">
                            <Zap size={13} />
                            <span>
                              {t('telefonie.dashboard.kpiTtfa')}: Ø {(c.meta.ttfa.avg_ms / 1000).toFixed(1)} s
                              {c.meta.ttfa.min_ms != null && ` (${(c.meta.ttfa.min_ms / 1000).toFixed(1)}–${(c.meta.ttfa.max_ms / 1000).toFixed(1)} s, ${c.meta.ttfa.turns} Turns)`}
                            </span>
                          </div>
                        )}
                        {/* #1453 — Endpointing-Bilanz des Anrufs (semantisches Turn-Taking) */}
                        {c.meta?.endpointing && (
                          <div className="flex items-center gap-2 pt-2 text-xs text-[var(--text-tertiary)]">
                            <AlertTriangle size={13} />
                            <span>
                              {t('telefonie.verlauf.endpointing')}: {c.meta.endpointing.holds || 0} {t('telefonie.verlauf.epHolds')}
                              {' · '}{c.meta.endpointing.continued || 0} {t('telefonie.verlauf.epContinued')}
                              {' · '}{c.meta.endpointing.expired || 0} {t('telefonie.verlauf.epExpired')}
                              {' · '}{c.meta.endpointing.merges || 0} {t('telefonie.verlauf.epMerges')}
                              {' · '}{c.meta.endpointing.early_barge || 0} {t('telefonie.verlauf.epEarlyBarge')}
                            </span>
                          </div>
                        )}
                        <EventTimeline callId={c.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-card,#E5E7EB)]">
            <p className="text-sm text-[var(--text-tertiary)]">
              {t('telefonie.verlauf.gesamt')}: {data.total} · {t('telefonie.verlauf.seite')} {data.page}/{totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPage((p) => Math.max(1, p - 1)); setExpanded(null); }}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 h-8 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} /> {t('telefonie.verlauf.zurueck')}
              </button>
              <button
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setExpanded(null); }}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 h-8 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40 transition-colors"
              >
                {t('telefonie.verlauf.weiter')} <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── #1436 Aufnahmen-Archiv: Liste + Audio-Player + Transkript + Löschen (DSGVO) ──

function parseTranscript(text) {
  if (!text) return [];
  return text.split('\n').map((line) => {
    const m = /^(Anrufer|Flow):\s*(.*)$/.exec(line.trim());
    if (m) return { speaker: m[1] === 'Anrufer' ? 'anrufer' : 'flow', text: m[2] };
    return line.trim() ? { speaker: null, text: line.trim() } : null;
  }).filter(Boolean);
}

function TranscriptView({ transkript }) {
  const { t } = useLanguage();
  const turns = useMemo(() => parseTranscript(transkript), [transkript]);
  if (!turns.length) return <p className="text-sm text-[var(--text-muted)] py-4">{t('telefonie.aufnahmen.keinTranskript')}</p>;
  return (
    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
      {turns.map((turn, i) => (
        <div key={i} className={`flex ${turn.speaker === 'flow' ? 'justify-end' : 'justify-start'}`}>
          <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm border ${
            turn.speaker === 'flow'
              ? 'bg-[#338edc]/10 border-[#338edc]/20'
              : 'bg-[var(--bg-inset,#F8F9FC)] border-[var(--border-card,#E5E7EB)]'
          }`}>
            {turn.speaker && (
              <p className="text-[11px] font-semibold mb-0.5 text-[var(--text-tertiary)]">
                {t(`telefonie.aufnahmen.sprecher.${turn.speaker}`)}
              </p>
            )}
            <p className="text-[var(--text-primary)]">{turn.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// #1466 — KI-Anruf-Analyse (manuell): 7 Scores + Befund + schreibfertiger Vorschlag.
const QM_DIMENSIONS = [
  'intent_korrekt', 'stimme_natuerlich', 'turn_taking', 'closing_sauber',
  'anrufer_emotion', 'ergebnis_angemessen', 'gefuehlte_latenz',
];

function scoreColor(s) {
  if (s == null) return 'var(--text-muted)';
  if (s >= 7) return '#16a34a';
  if (s >= 4) return '#d97706';
  return '#dc2626';
}

function ScoreRow({ dim, data, t }) {
  const s = data?.score;
  return (
    <div className="py-1.5 border-b border-[var(--border-card,#E5E7EB)] last:border-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-[var(--text-primary)]">{t(`telefonie.aufnahmen.qm.dim.${dim}`)}</span>
        <span className="text-sm font-semibold tabular-nums" style={{ color: scoreColor(s) }}>
          {s == null ? '—' : `${s}/10`}
        </span>
      </div>
      {data?.begruendung && (
        <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
          {data.begruendung}{data.zeitstempel ? ` (${data.zeitstempel})` : ''}
        </p>
      )}
    </div>
  );
}

function VoiceQualityPanel({ recordingId, callId, gestartetAt }) {
  const { t } = useLanguage();
  const [quality, setQuality] = useState(null);
  const [result, setResult] = useState(null); // frisches Analyse-Ergebnis (inkl. notion/kopier-Text)
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setQuality(null); setResult(null); setError(null); setCopied(false);
    fetch(`${API}/api/voip/recordings/${recordingId}/quality`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { quality: null }))
      .then((d) => setQuality(d.quality || null))
      .catch(() => {});
  }, [recordingId]);

  const run = async () => {
    setRunning(true); setError(null); setCopied(false);
    try {
      const res = await fetch(`${API}/api/voip/recordings/${recordingId}/analyse`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data);
      setQuality(null); // frisches Ergebnis übernimmt die Anzeige
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  // Anzeige-Quelle: frisches Ergebnis bevorzugt, sonst gespeichertes.
  const dims = result?.scores || quality?.scores?.dimensionen || null;
  const gesamt = result?.gesamtbewertung ?? quality?.scores?.gesamtbewertung ?? null;
  const befund = result?.befund_text ?? quality?.befund_text ?? '';
  const vorschlag = result?.vorschlag ?? quality?.vorschlag ?? null;
  const modell = result?.modell ?? quality?.modell ?? null;
  const tokens = result?.tokens_used ?? quality?.tokens_used ?? null;
  const kosten = result?.kosten_geschaetzt ?? quality?.kosten_geschaetzt ?? null;
  const notionUrl = result?.notion?.ok ? result.notion.page_url : quality?.notion_page_url;
  const notionFailed = result && !result.notion?.ok;
  const kopierText = result?.vorschlag_text || (vorschlag ? [
    `Befund: ${vorschlag.befund}`, `Vorschlag: ${vorschlag.vorschlag}`,
    `Ziel-Feld: ${vorschlag.ziel_feld}`, `Beleg: ${vorschlag.beleg}`,
    `Beleg-Anruf: voip_call ${callId} / recording ${recordingId}`,
  ].join('\n') : '');

  const copy = async () => {
    try { await navigator.clipboard.writeText(kopierText); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };

  const hasResult = !!dims;

  return (
    <div className="lg:col-span-2 pt-4 border-t border-[var(--border-card,#E5E7EB)] space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Sparkles size={15} className="text-[#338edc]" /> {t('telefonie.aufnahmen.qm.title')}
        </h3>
        <button
          onClick={run}
          disabled={running}
          className="inline-flex items-center gap-2 h-8 px-3 text-sm font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2b78bd] disabled:opacity-50 transition-colors"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {running ? t('telefonie.aufnahmen.qm.analysiere') : (hasResult ? t('telefonie.aufnahmen.qm.reanalyse') : t('telefonie.aufnahmen.qm.analysieren'))}
        </button>
      </div>

      {error && <p className="text-sm text-red-600">{t('telefonie.aufnahmen.qm.fehler')}: {error}</p>}
      {!hasResult && !error && (
        <p className="text-sm text-[var(--text-muted)]">{t('telefonie.aufnahmen.qm.nochKeine')}</p>
      )}

      {hasResult && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Scores */}
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Gauge size={15} style={{ color: scoreColor(gesamt) }} />
              <span className="text-sm text-[var(--text-tertiary)]">{t('telefonie.aufnahmen.qm.gesamt')}:</span>
              <span className="text-base font-bold tabular-nums" style={{ color: scoreColor(gesamt) }}>
                {gesamt == null ? '—' : `${gesamt}/10`}
              </span>
            </div>
            <div className="rounded-xl border border-[var(--border-card,#E5E7EB)] px-3">
              {QM_DIMENSIONS.map((d) => <ScoreRow key={d} dim={d} data={dims[d]} t={t} />)}
            </div>
            {(modell || kosten != null) && (
              <p className="text-[11px] text-[var(--text-muted)] mt-2">
                {modell && <>{t('telefonie.aufnahmen.qm.modell')}: {modell} · </>}
                {tokens != null && <>{t('telefonie.aufnahmen.qm.tokens')}: {tokens} · </>}
                {kosten != null && <>{t('telefonie.aufnahmen.qm.kosten')}: ~{Number(kosten).toFixed(4)} €</>}
              </p>
            )}
          </div>

          {/* Befund + Vorschlag */}
          <div className="space-y-3">
            {befund && (
              <div>
                <p className="text-xs font-semibold text-[var(--text-tertiary)] mb-1">{t('telefonie.aufnahmen.qm.befund')}</p>
                <p className="text-sm text-[var(--text-primary)]">{befund}</p>
              </div>
            )}
            {vorschlag && (
              <div className="rounded-xl border border-[#338edc]/20 bg-[#338edc]/5 p-3 space-y-1.5">
                <p className="text-xs font-semibold text-[#338edc] flex items-center gap-1.5">
                  <Lightbulb size={13} /> {t('telefonie.aufnahmen.qm.vorschlag')}
                </p>
                <p className="text-sm text-[var(--text-primary)]">{vorschlag.vorschlag}</p>
                <p className="text-xs text-[var(--text-tertiary)]">
                  {t('telefonie.aufnahmen.qm.zielFeld')}: <span className="font-medium">{vorschlag.ziel_feld}</span>
                  {vorschlag.beleg ? ` · ${t('telefonie.aufnahmen.qm.beleg')}: ${vorschlag.beleg}` : ''}
                </p>
                <div className="flex items-center gap-2 pt-1">
                  {notionUrl ? (
                    <a href={notionUrl} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-lg border border-green-200 text-green-700 hover:bg-green-50 transition-colors">
                      <ExternalLink size={12} /> {t('telefonie.aufnahmen.qm.notionOffen')}
                    </a>
                  ) : (
                    <button onClick={copy}
                      className="inline-flex items-center gap-1.5 h-7 px-2.5 text-xs font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
                      {copied ? <ClipboardCheck size={12} className="text-green-600" /> : <Copy size={12} />}
                      {copied ? t('telefonie.aufnahmen.qm.kopiert') : t('telefonie.aufnahmen.qm.kopieren')}
                    </button>
                  )}
                </div>
                {notionFailed && (
                  <p className="text-[11px] text-[var(--text-muted)]">{t('telefonie.aufnahmen.qm.keinNotion')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AufnahmeDetail({ recordingId, onDeleted }) {
  const { t } = useLanguage();
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    setRec(null); setError(null); setConfirmDelete(false); setDeleteError(null);
    fetch(`${API}/api/voip/recordings/${recordingId}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setRec)
      .catch((e) => setError(e.message));
  }, [recordingId]);

  const doDelete = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await fetch(`${API}/api/voip/recordings/${recordingId}`, { method: 'DELETE', credentials: 'include' });
      if (res.status === 403) throw new Error(t('telefonie.aufnahmen.loeschenKeineBerechtigung'));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      onDeleted();
    } catch (e) {
      setDeleteError(e.message);
      setDeleting(false);
    }
  };

  if (error) return <p className="text-sm text-red-600 py-3">{t('telefonie.aufnahmen.detailError')}: {error}</p>;
  if (!rec) return <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-3"><Loader2 size={14} className="animate-spin" /> {t('common.loading')}</div>;

  return (
    <div className="py-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Player + Meta */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Mic size={15} className="text-[#338edc]" /> {t('telefonie.aufnahmen.playerTitle')}
        </h3>
        <audio controls preload="metadata" className="w-full" src={`${API}/api/voip/recordings/${rec.id}/audio`}>
          {t('telefonie.aufnahmen.audioNichtUnterstuetzt')}
        </audio>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {[
            [t('telefonie.aufnahmen.colDauer'), fmtDauer(rec.dauer_sek)],
            [t('telefonie.aufnahmen.format'), rec.format],
            [t('telefonie.aufnahmen.groesse'), rec.groesse_bytes ? `${Math.round(rec.groesse_bytes / 1024)} KB` : '—'],
            [t('telefonie.aufnahmen.colAufbewahrung'), rec.aufbewahrung_bis ? new Date(rec.aufbewahrung_bis).toLocaleDateString('de-AT', { timeZone: 'Europe/Vienna' }) : '—'],
          ].map(([label, value]) => (
            <React.Fragment key={label}>
              <dt className="text-[var(--text-tertiary)]">{label}</dt>
              <dd className="text-[var(--text-primary)]">{value}</dd>
            </React.Fragment>
          ))}
        </dl>

        {/* Löschen (DSGVO) */}
        <div className="pt-2 border-t border-[var(--border-card,#E5E7EB)]">
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-2 h-8 px-3 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} /> {t('telefonie.aufnahmen.loeschen')}
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-[var(--text-secondary)]">{t('telefonie.aufnahmen.loeschenConfirm')}</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={doDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 h-8 px-3 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  {t('telefonie.aufnahmen.loeschenBestaetigen')}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="h-8 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                >
                  {t('telefonie.aufnahmen.abbrechen')}
                </button>
              </div>
            </div>
          )}
          {deleteError && <p className="text-sm text-red-600 mt-2">{deleteError}</p>}
        </div>
      </div>

      {/* Transkript */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('telefonie.aufnahmen.transkriptTitle')}</h3>
        <TranscriptView transkript={rec.transkript} />
      </div>

      {/* #1466 KI-Anruf-Analyse (manuell) */}
      <VoiceQualityPanel recordingId={rec.id} callId={rec.call_id} gestartetAt={rec.gestartet_at} />
    </div>
  );
}

export function TelefonieAufnahmenPage() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({ von: '', bis: '', mitarbeiter_id: '' });
  const [nummerInput, setNummerInput] = useState('');
  const [nummer, setNummer] = useState('');
  const [suchInput, setSuchInput] = useState('');
  const [suche, setSuche] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [maList, setMaList] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const tmr = setTimeout(() => { setNummer(nummerInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(tmr);
  }, [nummerInput]);

  useEffect(() => {
    const tmr = setTimeout(() => { setSuche(suchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(tmr);
  }, [suchInput]);

  useEffect(() => {
    fetch(`${API}/api/voip/stats`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMaList(d?.pro_mitarbeiter || []))
      .catch(() => {});
  }, []);

  const query = buildQuery({ ...filters, nummer, q: suche }, { page, page_size: 50 });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API}/api/voip/recordings?${query}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (alive) { setData(d); setError(null); } })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [query, reloadKey]);

  const setFilter = useCallback((key, value) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(1);
    setExpanded(null);
  }, []);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-6 space-y-6">
      <PageHead title={t('telefonie.aufnahmen.title')} subtitle={t('telefonie.aufnahmen.subtitle')} />

      {/* Filter-Leiste */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterVon')}</label>
          <input type="date" value={filters.von} onChange={(e) => setFilter('von', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterBis')}</label>
          <input type="date" value={filters.bis} onChange={(e) => setFilter('bis', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterMa')}</label>
          <select value={filters.mitarbeiter_id} onChange={(e) => setFilter('mitarbeiter_id', e.target.value)} className={inputCls}>
            <option value="">{t('telefonie.verlauf.alle')}</option>
            {maList.map((ma) => <option key={ma.mitarbeiter_id} value={ma.mitarbeiter_id}>{ma.mitarbeiter_name}</option>)}
          </select>
        </div>
        <div className="min-w-[160px]">
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.verlauf.filterNummer')}</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input type="text" value={nummerInput} onChange={(e) => setNummerInput(e.target.value)} placeholder={t('telefonie.verlauf.nummerPlaceholder')} className={`${inputCls} w-full pl-8`} />
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.aufnahmen.transkriptSuche')}</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input type="text" value={suchInput} onChange={(e) => setSuchInput(e.target.value)} placeholder={t('telefonie.aufnahmen.transkriptSuchePlaceholder')} className={`${inputCls} w-full pl-8`} />
          </div>
        </div>
      </div>

      {error && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">{t('telefonie.aufnahmen.loadError')}: {error}</span>
          </div>
        </div>
      )}

      {/* Tabelle */}
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                {['colZeit', 'colNummer', 'colMa', 'colDauer', 'colProvider', 'colCase', 'colAufbewahrung', 'colTranskript', ''].map((key, i) => (
                  <th key={i} className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] whitespace-nowrap">
                    {key ? t(`telefonie.aufnahmen.${key}`) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--text-tertiary)]">
                  <span className="inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> {t('common.loading')}</span>
                </td></tr>
              )}
              {!loading && data && data.recordings.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--text-muted)]">{t('telefonie.aufnahmen.empty')}</td></tr>
              )}
              {!loading && data && data.recordings.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors cursor-pointer"
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-primary)]">{fmtDateTime(r.gestartet_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-[var(--text-primary)]">
                      {r.richtung === 'inbound' ? (r.von_anzeige || r.von_nummer) : (r.an_anzeige || r.an_nummer)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-secondary)]">{r.mitarbeiter_name || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-primary)]">{fmtDauer(r.dauer_sek)}</td>
                    <td className="px-4 py-3"><ProviderBadge provider={r.voice_provider} model={r.voice_model} /></td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.case_id ? (
                        <Link to={`/cases/${r.case_id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[#338edc] hover:underline">
                          #{r.case_id} <ExternalLink size={12} />
                        </Link>
                      ) : <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-[var(--text-secondary)]">
                      {r.aufbewahrung_bis ? new Date(r.aufbewahrung_bis).toLocaleDateString('de-AT', { timeZone: 'Europe/Vienna' }) : '—'}
                    </td>
                    <td className="px-4 py-3 max-w-[280px]">
                      <p className="text-xs text-[var(--text-tertiary)] truncate">
                        {r.transkript_vorschau ? `${r.transkript_vorschau.replace(/\n/g, ' · ')}${r.transkript_gekuerzt ? '…' : ''}` : '—'}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)]">
                      {expanded === r.id ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                      <td colSpan={9} className="px-6">
                        <AufnahmeDetail
                          recordingId={r.id}
                          onDeleted={() => { setExpanded(null); setReloadKey((k) => k + 1); }}
                        />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data && data.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--border-card,#E5E7EB)]">
            <p className="text-sm text-[var(--text-tertiary)]">
              {t('telefonie.verlauf.gesamt')}: {data.total} · {t('telefonie.verlauf.seite')} {data.page}/{totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setPage((p) => Math.max(1, p - 1)); setExpanded(null); }}
                disabled={page <= 1}
                className="inline-flex items-center gap-1 h-8 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={14} /> {t('telefonie.verlauf.zurueck')}
              </button>
              <button
                onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); setExpanded(null); }}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1 h-8 px-3 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-40 transition-colors"
              >
                {t('telefonie.verlauf.weiter')} <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── #1448 Geschäftszeiten-Karte (vorläufig eigene Karte auf der Anruf-Regeln-Seite) ──
// Pro Wochentag Von-Bis + Schließen-Toggle + Feature-an/aus + Ansage + Feiertags-Hinweis.
// Postgres-DOW (0=So…6=Sa); Anzeige in Mo→So-Reihenfolge.
const BH_DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const BH_DAY_KEYS = ['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa'];

function GeschaeftszeitenCard() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [aktiv, setAktiv] = useState(false);
  const [ansage, setAnsage] = useState('');
  const [tage, setTage] = useState({}); // wochentag → {von_zeit,bis_zeit,geschlossen}
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/voip/geschaeftszeiten`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => {
        if (!alive) return;
        setAktiv(d.aktiv === true);
        setAnsage(d.ansage || '');
        const m = {};
        (d.tage || []).forEach((row) => { m[row.wochentag] = { von_zeit: row.von_zeit, bis_zeit: row.bis_zeit, geschlossen: row.geschlossen }; });
        setTage(m);
        setLoading(false);
      })
      .catch((e) => { if (alive) { setLoadError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const mut = (fn) => { fn(); setDirty(true); setSavedAt(null); };
  const setDay = (wd, patch) => mut(() => setTage((m) => ({ ...m, [wd]: { ...(m[wd] || { von_zeit: '08:00', bis_zeit: '17:00', geschlossen: false }), ...patch } })));

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        aktiv,
        ansage,
        tage: BH_DAY_ORDER.map((wd) => ({
          wochentag: wd,
          von_zeit: tage[wd]?.von_zeit || '08:00',
          bis_zeit: tage[wd]?.bis_zeit || '17:00',
          geschlossen: tage[wd]?.geschlossen === true,
        })),
      };
      const r = await fetch(`${API}/api/voip/geschaeftszeiten`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setDirty(false);
      setSavedAt(new Date());
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5 flex items-center gap-2 text-sm text-[var(--text-tertiary)]">
        <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="bg-[var(--bg-card,#fff)] border border-red-200 rounded-xl p-5 flex items-center gap-2 text-sm text-red-600">
        <AlertTriangle size={16} /> {t('telefonie.geschaeftszeiten.loadError')}: {loadError}
      </div>
    );
  }

  const timeInputCls = 'h-9 px-2 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 disabled:opacity-40';

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl max-w-4xl">
      <div className="flex items-start gap-3 px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="w-9 h-9 rounded-lg bg-[#EBF4FD] flex items-center justify-center shrink-0">
          <Clock size={18} className="text-[#338edc]" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.geschaeftszeiten.title')}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.geschaeftszeiten.desc')}</p>
        </div>
      </div>
      <div className="p-5 space-y-5">
        {/* Feature an/aus */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-[var(--text-primary)]">{t('telefonie.geschaeftszeiten.aktivToggle')}</span>
          <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)]">
            {[{ v: true, l: t('telefonie.geschaeftszeiten.an') }, { v: false, l: t('telefonie.geschaeftszeiten.aus') }].map((o) => (
              <button
                key={String(o.v)}
                type="button"
                onClick={() => mut(() => setAktiv(o.v))}
                className={`px-3 h-8 text-sm rounded-lg transition-colors ${aktiv === o.v ? 'bg-white shadow-sm font-medium text-[var(--text-primary)]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
        {!aktiv && (
          <p className="text-xs text-[var(--text-muted)]">{t('telefonie.geschaeftszeiten.ausHinweis')}</p>
        )}

        {/* Wochentage */}
        <div className="space-y-2">
          {BH_DAY_ORDER.map((wd) => {
            const d = tage[wd] || { von_zeit: '08:00', bis_zeit: '17:00', geschlossen: false };
            return (
              <div key={wd} className="grid grid-cols-[3rem_1fr_auto] sm:grid-cols-[5rem_auto_auto_auto] items-center gap-3 py-1">
                <span className="text-sm font-medium text-[var(--text-secondary)]">{t(`telefonie.geschaeftszeiten.tag.${BH_DAY_KEYS[wd]}`)}</span>
                <div className="flex items-center gap-2">
                  <input type="time" className={timeInputCls} value={d.von_zeit} disabled={d.geschlossen} onChange={(e) => setDay(wd, { von_zeit: e.target.value })} />
                  <span className="text-[var(--text-muted)]">–</span>
                  <input type="time" className={timeInputCls} value={d.bis_zeit} disabled={d.geschlossen} onChange={(e) => setDay(wd, { bis_zeit: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer sm:col-span-2 justify-self-end">
                  <input type="checkbox" className="accent-[#338edc]" checked={d.geschlossen === true} onChange={(e) => setDay(wd, { geschlossen: e.target.checked })} />
                  <span className="text-sm text-[var(--text-tertiary)]">{t('telefonie.geschaeftszeiten.geschlossen')}</span>
                </label>
              </div>
            );
          })}
        </div>

        {/* Ansage */}
        <div>
          <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.geschaeftszeiten.ansage')}</label>
          <textarea
            className="px-3 py-2 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 w-full resize-y"
            rows={2}
            maxLength={500}
            value={ansage}
            onChange={(e) => mut(() => setAnsage(e.target.value))}
            placeholder={t('telefonie.geschaeftszeiten.ansageHint')}
          />
        </div>

        {/* Feiertags-Hinweis */}
        <p className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
          <MoonStar size={13} className="mt-0.5 shrink-0" /> {t('telefonie.geschaeftszeiten.feiertagHinweis')}
        </p>

        {saveError && (
          <div className="flex items-center gap-2 text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg px-3 py-2">
            <AlertTriangle size={15} /> {saveError}
          </div>
        )}
        {savedAt && !dirty && (
          <div className="flex items-center gap-2 text-sm text-green-700 border border-green-200 bg-green-50 rounded-lg px-3 py-2">
            <CheckCircle2 size={15} /> {t('telefonie.geschaeftszeiten.gespeichert')}
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} {t('telefonie.geschaeftszeiten.speichern')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TelefonieRegelnPage() {
  const { t } = useLanguage();
  return (
    <div className="p-6 space-y-6">
      <PageHead title={t('telefonie.regeln.title')} subtitle={t('telefonie.regeln.subtitle')} />
      <GeschaeftszeitenCard />
      <TelefonieRegelnBuilder />
    </div>
  );
}

// ── #1431 Dashboard: KPIs + Volumen-Chart + Stunden-Heatmap + MA-Tabelle + Top-Intents ──

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const RANGE_PRESETS = [7, 30, 90];

function KpiCard({ Icon, label, value, sub }) {
  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={18} className="text-[#338edc]" />
        <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{label}</p>
      </div>
      <p className="text-2xl font-bold text-[var(--text-primary)]">{value}</p>
      {sub && <p className="text-sm text-[var(--text-muted)] mt-1">{sub}</p>}
    </div>
  );
}

function StundenHeatmap({ proStunde }) {
  const { t } = useLanguage();
  const map = useMemo(() => {
    const m = new Map();
    let max = 0;
    (proStunde || []).forEach((r) => {
      m.set(`${r.wochentag}-${r.stunde}`, r.anrufe);
      if (r.anrufe > max) max = r.anrufe;
    });
    return { m, max };
  }, [proStunde]);

  return (
    <div className="overflow-x-auto">
      <div className="grid gap-[3px] min-w-[640px]" style={{ gridTemplateColumns: 'auto repeat(24, 1fr)' }}>
        <div />
        {Array.from({ length: 24 }, (_, h) => (
          <div key={h} className="text-[10px] text-center text-[var(--text-muted)]">{h}</div>
        ))}
        {WOCHENTAGE.map((tag, di) => (
          <React.Fragment key={tag}>
            <div className="text-[11px] text-[var(--text-secondary)] pr-2 flex items-center">{tag}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const n = map.m.get(`${di + 1}-${h}`) || 0;
              const alpha = map.max ? 0.12 + 0.88 * (n / map.max) : 0;
              return (
                <div
                  key={h}
                  title={`${tag} ${h}:00 — ${n} ${t('telefonie.dashboard.anrufe')}`}
                  className="aspect-square rounded-[3px]"
                  style={{ backgroundColor: n > 0 ? `rgba(51, 142, 220, ${alpha.toFixed(2)})` : 'var(--bg-inset, #F8F9FC)' }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default function TelefonieDashboardPage() {
  const { t } = useLanguage();
  const [rangeDays, setRangeDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const von = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - (rangeDays - 1));
    return d.toISOString().slice(0, 10);
  }, [rangeDays]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API}/api/voip/stats?von=${von}`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (alive) { setStats(d); setError(null); } })
      .catch((e) => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [von]);

  const kpis = useMemo(() => {
    if (!stats) return null;
    const gesamt = stats.anrufe_gesamt || 0;
    const byErg = Object.fromEntries((stats.ergebnis_verteilung || []).map((r) => [r.ergebnis, r.anrufe]));
    const quote = (n) => (gesamt ? `${Math.round(((n || 0) / gesamt) * 100)} %` : '—');
    return {
      gesamt,
      kiQuote: quote(byErg.ki_geloest),
      durchstellQuote: quote(byErg.durchgestellt),
      verpasst: byErg.verpasst || 0,
      byErg,
    };
  }, [stats]);

  const chartData = useMemo(
    () => (stats?.pro_tag || []).slice().reverse().map((r) => ({
      tag: new Date(r.tag).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit' }),
      anrufe: r.anrufe,
    })),
    [stats]
  );

  const maxIntent = Math.max(1, ...((stats?.top_intents || []).map((r) => r.anrufe)));

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHead title={t('telefonie.dashboard.title')} subtitle={t('telefonie.dashboard.subtitle')} />
        <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card,#E5E7EB)]">
          {RANGE_PRESETS.map((d) => (
            <button
              key={d}
              onClick={() => setRangeDays(d)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${rangeDays === d ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-tertiary)]'}`}
            >
              {d} {t('telefonie.dashboard.tage')}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">{t('telefonie.dashboard.loadError')}: {error}</span>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
        </div>
      )}

      {!loading && stats && kpis && (
        <>
          {/* KPI-Karten — Werte IMMER --text-primary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <KpiCard Icon={Phone} label={t('telefonie.dashboard.kpiGesamt')} value={kpis.gesamt} sub={`${stats.beantwortet || 0} ${t('telefonie.dashboard.beantwortet')}`} />
            <KpiCard Icon={Clock} label={t('telefonie.dashboard.kpiDauer')} value={fmtDauer(stats.avg_gespraechs_sek)} sub={t('telefonie.dashboard.kpiDauerSub')} />
            <KpiCard Icon={Headphones} label={t('telefonie.dashboard.kpiWartezeit')} value={fmtDauer(stats.avg_wartezeit_sek)} sub={t('telefonie.dashboard.kpiWartezeitSub')} />
            <KpiCard Icon={Bot} label={t('telefonie.dashboard.kpiKiQuote')} value={kpis.kiQuote} sub={`${kpis.byErg.ki_geloest || 0} ${t('telefonie.dashboard.anrufe')}`} />
            <KpiCard Icon={ArrowRightLeft} label={t('telefonie.dashboard.kpiDurchstellQuote')} value={kpis.durchstellQuote} sub={`${kpis.byErg.durchgestellt || 0} ${t('telefonie.dashboard.anrufe')}`} />
            <KpiCard Icon={PhoneMissed} label={t('telefonie.dashboard.kpiVerpasst')} value={kpis.verpasst} sub={t('telefonie.dashboard.kpiVerpasstSub')} />
            {stats.avg_ttfa_ms != null && (
              <KpiCard Icon={Zap} label={t('telefonie.dashboard.kpiTtfa')} value={`${(stats.avg_ttfa_ms / 1000).toFixed(1)} s`} sub={t('telefonie.dashboard.kpiTtfaSub')} />
            )}
            {/* #1453 — Endpointing: fälschlich-unterbrochen-Proxy + Hold-Bilanz */}
            {stats.endpoint_calls > 0 && (
              <KpiCard
                Icon={AlertTriangle}
                label={t('telefonie.dashboard.kpiUnterbrechungen')}
                value={stats.endpoint_early_barges}
                sub={`${stats.endpoint_holds} ${t('telefonie.verlauf.epHolds')} · ${stats.endpoint_expired} ${t('telefonie.verlauf.epExpired')}`}
              />
            )}
          </div>

          {/* Volumen-Chart + Heatmap */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4">{t('telefonie.dashboard.volumenTitle')}</h2>
              {chartData.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] py-10 text-center">{t('telefonie.dashboard.keineDaten')}</p>
              ) : (
                <div className="w-full" style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="tag" stroke="#6B7280" fontSize={12} />
                      <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: 'var(--bg-elevated, #fff)', border: '1px solid var(--border-card, #E5E7EB)', borderRadius: 8 }} />
                      <Bar dataKey="anrufe" name={t('telefonie.dashboard.anrufe')} fill="#338edc" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4">{t('telefonie.dashboard.heatmapTitle')}</h2>
              {(stats.pro_stunde || []).length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] py-10 text-center">{t('telefonie.dashboard.keineDaten')}</p>
              ) : (
                <StundenHeatmap proStunde={stats.pro_stunde} />
              )}
            </div>
          </div>

          {/* Ergebnis-Verteilung + MA-Tabelle + Top-Intents */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-4">{t('telefonie.dashboard.ergebnisTitle')}</h2>
              {(stats.ergebnis_verteilung || []).length === 0
                ? <p className="text-sm text-[var(--text-muted)] py-6 text-center">{t('telefonie.dashboard.keineDaten')}</p>
                : (
                  <div className="space-y-2.5">
                    {stats.ergebnis_verteilung.map((r) => (
                      <div key={r.ergebnis} className="flex items-center justify-between">
                        <ErgebnisBadge ergebnis={r.ergebnis} />
                        <span className="text-sm font-semibold text-[var(--text-primary)]">{r.anrufe}</span>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center gap-2">
                <Users size={16} className="text-[#338edc]" />
                <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.dashboard.proMaTitle')}</h2>
              </div>
              {(stats.pro_mitarbeiter || []).length === 0
                ? <p className="text-sm text-[var(--text-muted)] py-6 text-center">{t('telefonie.dashboard.keineDaten')}</p>
                : (
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
                      {stats.pro_mitarbeiter.map((ma) => (
                        <tr key={ma.mitarbeiter_id}>
                          <td className="px-5 py-2.5 text-[var(--text-primary)]">{ma.mitarbeiter_name}</td>
                          <td className="px-5 py-2.5 text-right font-semibold text-[var(--text-primary)]">{ma.anrufe}</td>
                          <td className="px-5 py-2.5 text-right text-[var(--text-tertiary)] whitespace-nowrap">Ø {fmtDauer(ma.avg_gespraechs_sek)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb size={16} className="text-[#338edc]" />
                <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.dashboard.intentsTitle')}</h2>
              </div>
              {(stats.top_intents || []).length === 0
                ? <p className="text-sm text-[var(--text-muted)] py-6 text-center">{t('telefonie.dashboard.keineIntents')}</p>
                : (
                  <div className="space-y-2.5">
                    {stats.top_intents.map((r) => (
                      <div key={r.intent}>
                        <div className="flex items-center justify-between text-sm mb-0.5">
                          <span className="text-[var(--text-secondary)] truncate pr-2">{r.intent}</span>
                          <span className="font-semibold text-[var(--text-primary)]">{r.anrufe}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--bg-inset,#F8F9FC)]">
                          <div className="h-1.5 rounded-full" style={{ width: `${(r.anrufe / maxIntent) * 100}%`, backgroundColor: '#338edc' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Einstellungen: read-only VOIP-Feature-Status (keine Secrets) ──

// ── #1432 Gruppen/Teams: CRUD in den Telefonie-Einstellungen ──

function GruppenSection() {
  const { t } = useLanguage();
  const [groups, setGroups] = useState(null);
  const [targets, setTargets] = useState([]);
  const [error, setError] = useState(null);
  const [newName, setNewName] = useState('');
  const [newStrategie, setNewStrategie] = useState('parallel');
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [addUserFor, setAddUserFor] = useState({});

  const load = useCallback(() => {
    fetch(`${API}/api/voip/groups`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setGroups(d.groups || []); setError(null); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    fetch(`${API}/api/twilio/voice/cascade-targets`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { targets: [] }))
      .then((d) => setTargets(d.targets || []))
      .catch(() => {});
  }, [load]);

  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setError(null);
      load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const createGroup = async () => {
    if (!newName.trim()) return;
    if (await call('POST', '/api/voip/groups', { name: newName.trim(), strategie: newStrategie })) {
      setNewName('');
      setNewStrategie('parallel');
    }
  };

  const saveMembers = (g, mitglieder) =>
    call('PUT', `/api/voip/groups/${g.id}/members`, {
      members: mitglieder.map((m, i) => ({ mitarbeiter_id: m.mitarbeiter_id, prioritaet: (i + 1) * 10 })),
    });

  const addMember = (g) => {
    const uid = Number(addUserFor[g.id]);
    if (!uid) return;
    setAddUserFor((s) => ({ ...s, [g.id]: '' }));
    saveMembers(g, [...g.mitglieder, { mitarbeiter_id: uid }]);
  };

  const removeMember = (g, uid) => saveMembers(g, g.mitglieder.filter((m) => m.mitarbeiter_id !== uid));

  const moveMember = (g, idx, dir) => {
    const arr = [...g.mitglieder];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    saveMembers(g, arr);
  };

  const doDelete = async (g) => {
    if (confirmDelete !== g.id) { setConfirmDelete(g.id); return; }
    setConfirmDelete(null);
    await call('DELETE', `/api/voip/groups/${g.id}`);
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center gap-2">
        <Users size={18} className="text-[#338edc]" />
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.gruppen.title')}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.gruppen.hint')}</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* Neue Gruppe */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={inputCls}
            placeholder={t('telefonie.gruppen.namePlaceholder')}
            value={newName}
            maxLength={100}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') createGroup(); }}
          />
          <select className={inputCls} value={newStrategie} onChange={(e) => setNewStrategie(e.target.value)}>
            <option value="parallel">{t('telefonie.gruppen.strategieParallel')}</option>
            <option value="round_robin">{t('telefonie.gruppen.strategieRoundRobin')}</option>
          </select>
          <button
            onClick={createGroup}
            disabled={busy || !newName.trim()}
            className="h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white disabled:opacity-50 hover:bg-[#2b7cc4] transition-colors"
          >
            {t('telefonie.gruppen.create')}
          </button>
        </div>

        {!groups && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-4">
            <Loader2 size={14} className="animate-spin" /> {t('common.loading')}
          </div>
        )}
        {groups && groups.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] py-2">{t('telefonie.gruppen.empty')}</p>
        )}

        {(groups || []).map((g) => {
          const memberIds = new Set(g.mitglieder.map((m) => m.mitarbeiter_id));
          const addable = targets.filter((u) => !memberIds.has(u.id));
          return (
            <div key={g.id} className="border border-[var(--border-card,#E5E7EB)] rounded-xl p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-[var(--text-primary)]">{g.name}</span>
                {g.durchwahl != null && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">{t('telefonie.gruppen.durchwahl')}: {g.durchwahl}</span>
                )}
                <select
                  className={inputCls}
                  value={g.strategie}
                  disabled={busy}
                  onChange={(e) => call('PUT', `/api/voip/groups/${g.id}`, { strategie: e.target.value })}
                >
                  <option value="parallel">{t('telefonie.gruppen.strategieParallel')}</option>
                  <option value="round_robin">{t('telefonie.gruppen.strategieRoundRobin')}</option>
                </select>
                <label className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={g.aktiv}
                    disabled={busy}
                    onChange={(e) => call('PUT', `/api/voip/groups/${g.id}`, { aktiv: e.target.checked })}
                  />
                  {t('telefonie.gruppen.aktiv')}
                </label>
                <div className="ml-auto flex items-center gap-2">
                  {confirmDelete === g.id && (
                    <button onClick={() => setConfirmDelete(null)} className="text-xs text-[var(--text-tertiary)] hover:underline">
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => doDelete(g)}
                    disabled={busy}
                    className={`inline-flex items-center gap-1 h-8 px-3 text-xs font-medium rounded-lg border transition-colors ${
                      confirmDelete === g.id
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:text-red-600'
                    }`}
                  >
                    <Trash2 size={12} /> {confirmDelete === g.id ? t('telefonie.gruppen.deleteConfirm') : t('common.delete')}
                  </button>
                </div>
              </div>

              {/* Mitglieder (Reihenfolge = Priorität, bei round_robin auch Rotations-Reihenfolge) */}
              <div className="space-y-1.5">
                {g.mitglieder.length === 0 && (
                  <p className="text-sm text-[var(--text-muted)]">{t('telefonie.gruppen.noMembers')}</p>
                )}
                {g.mitglieder.map((m, idx) => (
                  <div key={m.mitarbeiter_id} className="flex items-center gap-2 text-sm">
                    <span className="w-6 text-xs text-[var(--text-muted)] text-right">{idx + 1}.</span>
                    <span className="text-[var(--text-primary)]">{m.name || `MA ${m.mitarbeiter_id}`}</span>
                    {(!m.aktiv || !m.kaskade_aktiv) && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{t('telefonie.gruppen.nichtKaskade')}</span>
                    )}
                    <span className="ml-auto flex items-center gap-1">
                      <button onClick={() => moveMember(g, idx, -1)} disabled={busy || idx === 0} className="p-1 text-[var(--text-tertiary)] disabled:opacity-30 hover:text-[var(--text-primary)]"><ChevronUp size={14} /></button>
                      <button onClick={() => moveMember(g, idx, 1)} disabled={busy || idx === g.mitglieder.length - 1} className="p-1 text-[var(--text-tertiary)] disabled:opacity-30 hover:text-[var(--text-primary)]"><ChevronDown size={14} /></button>
                      <button onClick={() => removeMember(g, m.mitarbeiter_id)} disabled={busy} className="p-1 text-[var(--text-tertiary)] hover:text-red-600"><Trash2 size={14} /></button>
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-2 pt-1">
                  <select
                    className={inputCls}
                    value={addUserFor[g.id] || ''}
                    onChange={(e) => setAddUserFor((s) => ({ ...s, [g.id]: e.target.value }))}
                  >
                    <option value="">{t('telefonie.gruppen.addMemberPlaceholder')}</option>
                    {addable.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                  <button
                    onClick={() => addMember(g)}
                    disabled={busy || !addUserFor[g.id]}
                    className="h-9 px-3 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] disabled:opacity-50 hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                  >
                    {t('telefonie.gruppen.addMember')}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** #1433/#1445 — Durchwahlen (Nebenstellen): 2-stellige Nummern 10–99 für MA + Gruppen.
 *  Row MUSS top-level bleiben: als Inner-Component remountete React die Zeile
 *  bei jedem Tastendruck → Fokus-Verlust (#1445 Bug 2). */
function DurchwahlRow({ kind, id, name, durchwahl, badge, edits, setEdits, busy, save, saved, t }) {
  const k = `${kind}_${id}`;
  const val = edits[k] !== undefined ? edits[k] : (durchwahl != null ? String(durchwahl) : '');
  const dirty = edits[k] !== undefined && edits[k] !== (durchwahl != null ? String(durchwahl) : '');
  const valid = val === '' || /^[1-9]\d$/.test(val);
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-[var(--text-primary)] flex-1 min-w-0 truncate">{name}</span>
      {saved === k && (
        <span className="flex items-center gap-1 text-xs text-emerald-600">
          <Check size={13} /> {t('telefonie.durchwahlen.saved')}
        </span>
      )}
      {badge}
      <input
        className={`${inputCls} w-24 text-center ${!valid ? 'border-red-400' : ''}`}
        value={val}
        maxLength={2}
        inputMode="numeric"
        placeholder="—"
        onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value.replace(/\D/g, '') }))}
        onKeyDown={(e) => { if (e.key === 'Enter' && dirty && valid) save(kind, id, val); }}
      />
      <button
        onClick={() => save(kind, id, val)}
        disabled={busy || !dirty || !valid}
        className="h-8 px-3 text-xs font-medium rounded-lg bg-[#338edc] text-white disabled:opacity-40 hover:bg-[#2b7cc4] transition-colors"
      >
        {t('common.save')}
      </button>
    </div>
  );
}

function DurchwahlenSection() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [edits, setEdits] = useState({});
  const [saved, setSaved] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/voip/extensions`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setData(d); setError(null); setEdits({}); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (kind, id, raw) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/voip/extensions/${kind}/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ durchwahl: raw === '' ? null : raw }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setError(null);
      setSaved(`${kind}_${id}`);
      setTimeout(() => setSaved(null), 2500);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const rowProps = { edits, setEdits, busy, save, saved, t };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center gap-2">
        <Hash size={18} className="text-[#338edc]" />
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.durchwahlen.title')}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.durchwahlen.hint')}</p>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} /> {error}
          </div>
        )}
        {!data && !error && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-4">
            <Loader2 size={14} className="animate-spin" /> {t('common.loading')}
          </div>
        )}
        {data && (
          <>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.durchwahlen.groups')}</p>
              {(data.groups || []).length === 0 && <p className="text-sm text-[var(--text-muted)]">{t('telefonie.durchwahlen.noGroups')}</p>}
              {(data.groups || []).map((g) => (
                <DurchwahlRow
                  key={`g${g.id}`} kind="group" id={g.id} name={g.name} durchwahl={g.durchwahl} {...rowProps}
                  badge={!g.aktiv ? <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{t('telefonie.durchwahlen.inaktiv')}</span> : null}
                />
              ))}
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.durchwahlen.users')}</p>
              {(data.users || []).map((u) => (
                <DurchwahlRow
                  key={`u${u.id}`} kind="user" id={u.id} name={u.name} durchwahl={u.durchwahl} {...rowProps}
                  badge={(!u.softphone_enabled && !u.kaskade_aktiv) ? <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">{t('telefonie.durchwahlen.nichtErreichbar')}</span> : null}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** #1443 — Blocklist (Gate 4): Spam-/Missbrauchs-Nummern, Check sitzt VOR der
 *  Hotline-Whitelist. anrufe_30d = display-only Häufigkeits-Hinweis (kein Auto-Block). */
function BlocklistSection() {
  const { t } = useLanguage();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newNummer, setNewNummer] = useState('');
  const [newGrund, setNewGrund] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/voip/blocklist`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setItems(d.blocklist || []); setError(null); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setError(null);
      load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addEntry = async () => {
    if (!newNummer.trim()) return;
    if (await call('POST', '/api/voip/blocklist', { nummer: newNummer.trim(), grund: newGrund.trim() || undefined })) {
      setNewNummer('');
      setNewGrund('');
    }
  };

  const doDelete = async (entry) => {
    if (confirmDelete !== entry.id) { setConfirmDelete(entry.id); return; }
    setConfirmDelete(null);
    await call('DELETE', `/api/voip/blocklist/${entry.id}`);
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center gap-2">
        <Ban size={18} className="text-[#338edc]" />
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.blocklist.title')}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.blocklist.hint')}</p>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* Neue Nummer */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={inputCls}
            placeholder={t('telefonie.blocklist.nummerPlaceholder')}
            value={newNummer}
            maxLength={40}
            onChange={(e) => setNewNummer(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addEntry(); }}
          />
          <input
            className={`${inputCls} min-w-[220px]`}
            placeholder={t('telefonie.blocklist.grundPlaceholder')}
            value={newGrund}
            maxLength={500}
            onChange={(e) => setNewGrund(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addEntry(); }}
          />
          <button
            onClick={addEntry}
            disabled={busy || !newNummer.trim()}
            className="h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white disabled:opacity-50 hover:bg-[#2b7cc4] transition-colors"
          >
            {t('telefonie.blocklist.add')}
          </button>
        </div>

        {!items && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-4">
            <Loader2 size={14} className="animate-spin" /> {t('common.loading')}
          </div>
        )}
        {items && items.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] py-2">{t('telefonie.blocklist.empty')}</p>
        )}

        {items && items.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                {['colNummer', 'colGrund', 'colVon', 'colCalls30d', 'colAktiv', ''].map((key, i) => (
                  <th key={i} className="px-3 py-2 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)] whitespace-nowrap">
                    {key ? t(`telefonie.blocklist.${key}`) : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
              {items.map((b) => (
                <tr key={b.id} className={`transition-colors ${b.aktiv ? '' : 'opacity-50'}`}>
                  <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-[var(--text-primary)]">{b.nummer}</td>
                  <td className="px-3 py-2.5 text-[var(--text-secondary)]">{b.grund || '—'}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[var(--text-tertiary)]">
                    {b.hinzugefuegt_von_name || '—'} · {fmtDateTime(b.hinzugefuegt_am)}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {b.anrufe_30d > 0 ? (
                      <span
                        className={`px-2 py-0.5 text-xs font-medium rounded-full ${b.anrufe_30d >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}
                        title={t('telefonie.blocklist.callsHint')}
                      >
                        {b.anrufe_30d}
                      </span>
                    ) : <span className="text-[var(--text-muted)]">0</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <label className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)] cursor-pointer">
                      <input
                        type="checkbox"
                        checked={b.aktiv}
                        disabled={busy}
                        onChange={(e) => call('PUT', `/api/voip/blocklist/${b.id}`, { aktiv: e.target.checked })}
                      />
                      {t('telefonie.blocklist.aktiv')}
                    </label>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {confirmDelete === b.id && (
                      <button onClick={() => setConfirmDelete(null)} className="text-xs text-[var(--text-tertiary)] hover:underline mr-2">
                        {t('common.cancel')}
                      </button>
                    )}
                    <button
                      onClick={() => doDelete(b)}
                      disabled={busy}
                      className={`inline-flex items-center gap-1 h-8 px-3 text-xs font-medium rounded-lg border transition-colors ${
                        confirmDelete === b.id
                          ? 'border-red-300 bg-red-50 text-red-700'
                          : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:text-red-600'
                      }`}
                    >
                      <Trash2 size={12} /> {confirmDelete === b.id ? t('telefonie.blocklist.deleteConfirm') : t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// #1485 Stufe A — Terminbuchungen: pro Abteilung "bietet Telefon-Termine an",
// interne MA als buchbar markieren (Calendly-Status aus User-Account) + externe
// Personen mit eigenem Calendly-Link/Key (verschlüsselt) hinzufügen. Verbindet
// die Konfig mit dem bestehenden book_calendly-Mechanismus (Stufe A: EIN Kalender).
function TerminbuchungenSection() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addFor, setAddFor] = useState(null); // abteilung_id für externes Formular
  const [form, setForm] = useState({ name: '', calendly_url: '', calendly_event_type_uri: '', calendly_api_key: '', kontakt_email: '', kontakt_phone: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  // #1485 Nachzug — Event-Type-Dropdown statt manueller URI.
  const [etList, setEtList] = useState(null);
  const [etLoading, setEtLoading] = useState(false);
  const [manualUri, setManualUri] = useState(false);
  // #1485 Nachzug — globaler Kill-Switch-Zustand (voice_calendly_enabled).
  const [calEnabled, setCalEnabled] = useState(false);
  // #1485 Nachzug — interne (abteilungsfremde) MA hinzufügen.
  const [internAddFor, setInternAddFor] = useState(null); // abteilung_id mit offenem Intern-Picker
  const [internKandidaten, setInternKandidaten] = useState(null);
  const [internLoading, setInternLoading] = useState(false);
  const [internSel, setInternSel] = useState('');
  const [internConfirmDelete, setInternConfirmDelete] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/api/voip/terminbuchung`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { setData(d.abteilungen || []); setCalEnabled(!!d.calendly_enabled); setError(null); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => { load(); }, [load]);

  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setError(null);
      load();
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const submitExtern = async (abteilungId) => {
    if (!form.name.trim()) return;
    const ok = await call('POST', '/api/voip/terminbuchung/extern', { abteilung_id: abteilungId, ...form });
    if (ok) { setAddFor(null); setForm({ name: '', calendly_url: '', calendly_event_type_uri: '', calendly_api_key: '', kontakt_email: '', kontakt_phone: '' }); setEtList(null); setManualUri(false); }
  };

  // #1485 Nachzug — Calendly-Event-Types via API laden (Dropdown), PAT aus dem Formular.
  const loadEventTypes = async () => {
    setEtLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/voip/terminbuchung/calendly-event-types`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ api_key: form.calendly_api_key || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.ok) { setEtList(d.event_types || []); if (!d.event_types?.length) setError(t('telefonie.termine.noEventTypes')); }
      else { setEtList(null); setError(d.error || t('telefonie.termine.eventTypesFailed')); }
    } catch (e) { setEtList(null); setError(e.message); }
    finally { setEtLoading(false); }
  };

  const openAdd = (abtId) => {
    setAddFor(abtId);
    setForm({ name: '', calendly_url: '', calendly_event_type_uri: '', calendly_api_key: '', kontakt_email: '', kontakt_phone: '' });
    setEtList(null); setManualUri(false);
  };

  const delExtern = async (p) => {
    if (confirmDelete !== p.id) { setConfirmDelete(p.id); return; }
    setConfirmDelete(null);
    await call('DELETE', `/api/voip/terminbuchung/extern/${p.id}`);
  };

  // #1485 Nachzug — interne Kandidaten für eine Abteilung laden (Picker).
  const openInternAdd = async (abtId) => {
    setInternAddFor(abtId); setInternSel(''); setInternKandidaten(null); setInternLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/voip/terminbuchung/intern-kandidaten/${abtId}`, { credentials: 'include' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setInternKandidaten(d.kandidaten || []);
    } catch (e) { setError(e.message); setInternKandidaten([]); }
    finally { setInternLoading(false); }
  };

  const submitIntern = async (abtId) => {
    if (!internSel) return;
    const ok = await call('POST', '/api/voip/terminbuchung/intern', { abteilung_id: abtId, mitarbeiter_id: Number(internSel) });
    if (ok) { setInternAddFor(null); setInternSel(''); setInternKandidaten(null); }
  };

  const delIntern = async (m) => {
    if (internConfirmDelete !== m.tbp_id) { setInternConfirmDelete(m.tbp_id); return; }
    setInternConfirmDelete(null);
    await call('DELETE', `/api/voip/terminbuchung/intern/${m.tbp_id}`);
  };

  const calBadge = (vorhanden, verified) => vorhanden
    ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${verified ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}><Check size={11} /> {verified ? t('telefonie.termine.calendlyVerified') : t('telefonie.termine.calendlyPresent')}</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">{t('telefonie.termine.calendlyMissing')}</span>;

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)] flex items-center gap-2">
        <CalendarClock size={18} className="text-[#338edc]" />
        <div>
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.termine.title')}</h2>
          <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.termine.hint')}</p>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* #1485 Nachzug — globaler Kill-Switch: Telefon-Terminbuchung an/aus. */}
        <div className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border ${calEnabled ? 'border-[#338edc]/40 bg-[#338edc]/5' : 'border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)]'}`}>
          <div>
            <div className="text-sm font-semibold text-[var(--text-primary)]">{t('telefonie.termine.killSwitch')}</div>
            <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
              {calEnabled ? t('telefonie.termine.killSwitchOn') : t('telefonie.termine.killSwitchOff')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={calEnabled}
              disabled={busy}
              onChange={(e) => call('PATCH', '/api/voip/terminbuchung/enabled', { enabled: e.target.checked })}
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#338edc] peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
          </label>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600"><AlertTriangle size={14} /> {error}</div>
        )}

        {!data && (
          <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-4">
            <Loader2 size={14} className="animate-spin" /> {t('common.loading')}
          </div>
        )}
        {data && data.length === 0 && (
          <p className="text-sm text-[var(--text-muted)] py-2">{t('telefonie.termine.noDepartments')}</p>
        )}

        {data && data.map((abt) => (
          <div key={abt.id} className="border border-[var(--border-card,#E5E7EB)] rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-[var(--bg-inset,#F8F9FC)] flex items-center justify-between gap-3">
              <div>
                <span className="text-sm font-semibold text-[var(--text-primary)]">{abt.name}</span>
                {abt.bereich && <span className="text-xs text-[var(--text-tertiary)] ml-2">{abt.bereich}</span>}
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!abt.bietet_termine}
                  disabled={busy}
                  onChange={(e) => call('PATCH', `/api/voip/terminbuchung/abteilung/${abt.id}`, { bietet_termine: e.target.checked })}
                />
                {t('telefonie.termine.offersAppointments')}
              </label>
            </div>

            {abt.bietet_termine && (
              <div className="p-4 space-y-4">
                {/* Interne Mitarbeiter */}
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-tertiary)] mb-2">{t('telefonie.termine.internal')}</p>
                  {abt.mitarbeiter.length === 0 ? (
                    <p className="text-sm text-[var(--text-muted)]">{t('telefonie.termine.noStaff')}</p>
                  ) : (
                    <div className="space-y-1.5">
                      {abt.mitarbeiter.map((m) => (
                        <div key={m.mitarbeiter_id} className={`flex items-center justify-between gap-3 ${m.calendly_vorhanden ? '' : 'opacity-60'}`}>
                          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!m.buchbar}
                              disabled={busy || !m.calendly_vorhanden}
                              onChange={(e) => call('PUT', `/api/voip/terminbuchung/mitarbeiter/${m.mitarbeiter_id}`, { buchbar: e.target.checked })}
                            />
                            {m.name}
                          </label>
                          {calBadge(m.calendly_vorhanden, m.calendly_verified)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* #1485 Nachzug — zusätzliche interne (abteilungsfremde) Personen */}
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-tertiary)] mb-2">{t('telefonie.termine.internalManual')}</p>
                  {abt.interne_manuell.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {abt.interne_manuell.map((m) => (
                        <div key={m.tbp_id} className={`flex items-center justify-between gap-3 ${m.calendly_vorhanden ? '' : 'opacity-60'}`}>
                          <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!m.buchbar}
                              disabled={busy || !m.calendly_vorhanden}
                              onChange={(e) => call('PATCH', `/api/voip/terminbuchung/intern/${m.tbp_id}`, { aktiv: e.target.checked })}
                            />
                            {m.name}
                            {m.hr_abteilung_name && <span className="text-xs text-[var(--text-tertiary)]">{t('telefonie.termine.fromDepartment')} {m.hr_abteilung_name}</span>}
                          </label>
                          <div className="flex items-center gap-2">
                            {calBadge(m.calendly_vorhanden, m.calendly_verified)}
                            <button
                              onClick={() => delIntern(m)}
                              disabled={busy}
                              className={`inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-lg border transition-colors ${internConfirmDelete === m.tbp_id ? 'border-red-300 bg-red-50 text-red-700' : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:text-red-600'}`}
                            >
                              <Trash2 size={11} /> {internConfirmDelete === m.tbp_id ? t('telefonie.termine.deleteConfirm') : t('common.delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {internAddFor === abt.id ? (
                    <div className="border border-[var(--border-card,#E5E7EB)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
                      {internLoading ? (
                        <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)]"><Loader2 size={14} className="animate-spin" /> {t('common.loading')}</div>
                      ) : (Array.isArray(internKandidaten) && internKandidaten.length > 0) ? (
                        <div className="flex items-center gap-2">
                          <select className={inputCls + ' flex-1'} value={internSel} onChange={(e) => setInternSel(e.target.value)}>
                            <option value="">{t('telefonie.termine.chooseInternal')}</option>
                            {internKandidaten.map((k) => (
                              <option key={k.mitarbeiter_id} value={k.mitarbeiter_id}>
                                {k.name}{k.hr_abteilung_name ? ` (${k.hr_abteilung_name})` : ''}{k.calendly_vorhanden ? '' : ` — ${t('telefonie.termine.calendlyMissing')}`}
                              </option>
                            ))}
                          </select>
                          <button onClick={() => submitIntern(abt.id)} disabled={busy || !internSel} className="h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white disabled:opacity-50 hover:bg-[#2b7cc4] transition-colors">{t('common.add')}</button>
                          <button onClick={() => { setInternAddFor(null); setInternSel(''); setInternKandidaten(null); }} className="h-9 px-4 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)]">{t('common.cancel')}</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm text-[var(--text-muted)]">{t('telefonie.termine.noInternalCandidates')}</span>
                          <button onClick={() => { setInternAddFor(null); setInternKandidaten(null); }} className="h-8 px-3 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)]">{t('common.cancel')}</button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => openInternAdd(abt.id)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                    >
                      <Plus size={14} /> {t('telefonie.termine.addInternal')}
                    </button>
                  )}
                </div>

                {/* Externe Personen */}
                <div>
                  <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-tertiary)] mb-2">{t('telefonie.termine.external')}</p>
                  {abt.externe.length > 0 && (
                    <div className="space-y-1.5 mb-2">
                      {abt.externe.map((p) => (
                        <div key={p.id} className={`flex items-center justify-between gap-3 ${p.aktiv ? '' : 'opacity-50'}`}>
                          <div className="text-sm text-[var(--text-secondary)]">
                            {p.name}
                            {p.kontakt_email && <span className="text-xs text-[var(--text-tertiary)] ml-2">{p.kontakt_email}</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {calBadge(p.calendly_vorhanden, p.calendly_verified)}
                            <button
                              onClick={() => delExtern(p)}
                              disabled={busy}
                              className={`inline-flex items-center gap-1 h-7 px-2.5 text-xs font-medium rounded-lg border transition-colors ${confirmDelete === p.id ? 'border-red-300 bg-red-50 text-red-700' : 'border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:text-red-600'}`}
                            >
                              <Trash2 size={11} /> {confirmDelete === p.id ? t('telefonie.termine.deleteConfirm') : t('common.delete')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {addFor === abt.id ? (
                    <div className="border border-[var(--border-card,#E5E7EB)] rounded-lg p-3 space-y-2 bg-[var(--bg-inset,#F8F9FC)]">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input className={inputCls} placeholder={t('telefonie.termine.namePlaceholder')} value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <input className={inputCls} placeholder={t('telefonie.termine.emailPlaceholder')} value={form.kontakt_email} maxLength={160} onChange={(e) => setForm({ ...form, kontakt_email: e.target.value })} />
                        <input className={inputCls} placeholder={t('telefonie.termine.phonePlaceholder')} value={form.kontakt_phone} maxLength={40} onChange={(e) => setForm({ ...form, kontakt_phone: e.target.value })} />
                        <input className={inputCls} placeholder={t('telefonie.termine.urlPlaceholder')} value={form.calendly_url} maxLength={300} onChange={(e) => setForm({ ...form, calendly_url: e.target.value })} />
                        <input className={inputCls} type="password" placeholder={t('telefonie.termine.keyPlaceholder')} value={form.calendly_api_key} maxLength={300} onChange={(e) => setForm({ ...form, calendly_api_key: e.target.value })} />
                      </div>
                      {/* #1485 Nachzug — Termin-Typ als Dropdown statt roher URI */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-[var(--text-secondary)]">{t('telefonie.termine.eventType')}</span>
                          <button type="button" onClick={loadEventTypes} disabled={etLoading || !form.calendly_api_key} className="text-[11px] text-[#338edc] hover:underline disabled:opacity-50 disabled:no-underline">
                            {etLoading ? t('common.loading') : t('telefonie.termine.loadEventTypes')}
                          </button>
                        </div>
                        {Array.isArray(etList) && etList.length > 0 ? (
                          <select className={inputCls + ' w-full'} value={form.calendly_event_type_uri} onChange={(e) => setForm({ ...form, calendly_event_type_uri: e.target.value })}>
                            <option value="">{t('telefonie.termine.chooseEventType')}</option>
                            {etList.map((et) => (<option key={et.uri} value={et.uri}>{et.name}{et.duration ? ` (${et.duration} Min)` : ''}</option>))}
                          </select>
                        ) : (
                          <p className="text-[11px] text-[var(--text-tertiary)]">{form.calendly_api_key ? t('telefonie.termine.loadEventTypesHint') : t('telefonie.termine.enterKeyFirst')}</p>
                        )}
                        <button type="button" onClick={() => setManualUri((v) => !v)} className="text-[10px] text-[var(--text-tertiary)] hover:underline">
                          {manualUri ? t('telefonie.termine.hideManualUri') : t('telefonie.termine.manualUri')}
                        </button>
                        {manualUri && (
                          <input className={inputCls + ' w-full'} placeholder={t('telefonie.termine.eventUriPlaceholder')} value={form.calendly_event_type_uri} maxLength={300} onChange={(e) => setForm({ ...form, calendly_event_type_uri: e.target.value })} />
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => submitExtern(abt.id)} disabled={busy || !form.name.trim()} className="h-9 px-4 text-sm font-medium rounded-lg bg-[#338edc] text-white disabled:opacity-50 hover:bg-[#2b7cc4] transition-colors">{t('common.save')}</button>
                        <button onClick={() => { setAddFor(null); setConfirmDelete(null); setEtList(null); setManualUri(false); }} className="h-9 px-4 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)]">{t('common.cancel')}</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => openAdd(abt.id)}
                      className="inline-flex items-center gap-1.5 h-8 px-3 text-sm font-medium rounded-lg border border-[var(--border-card,#E5E7EB)] text-[#338edc] hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors"
                    >
                      <Plus size={14} /> {t('telefonie.termine.addExternal')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// #1477 — S2S-Stimme (OpenAI Realtime) per UI wählbar. GETRENNT von der
// ElevenLabs-Stimme (voice_tts_voice_id, cascade-Pfad, #1470-Lock): hier nur
// voice_s2s_openai_voice. Gilt ausschließlich im s2s-Pfad (voice_provider=
// s2s_openai); das Gateway/die Realtime-Session liest den Key pro Anruf →
// kein Neustart nötig. GA-gpt-realtime-Stimmen (marin/cedar realtime-exklusiv).
const S2S_VOICES = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse', 'marin', 'cedar'];

/**
 * #1478 — Stimmen-Vorschau-Button: holt MP3 von /api/voip/voice-preview und spielt
 * es im Browser ab (keine Telefon-Session). provider='s2s_openai' → OpenAI-Stimme
 * (voice), 'cascade' → gelockte ElevenLabs-Hotline-Stimme (#1470).
 */
function VoicePreviewButton({ provider, voice, label }) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  const play = async () => {
    setBusy(true); setErr(false);
    try {
      const r = await fetch(`${API}/api/voip/voice-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ provider, ...(voice ? { voice } : {}) }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) audioRef.current.pause();
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => URL.revokeObjectURL(url);
      await a.play();
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={play}
        disabled={busy}
        className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-sm font-medium border border-[var(--border-card,#E5E7EB)] text-[var(--text-secondary)] hover:bg-[var(--bg-inset,#F8F9FC)] disabled:opacity-50"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        {label || t('telefonie.preview.play')}
      </button>
      {err && <span className="text-xs text-red-600">{t('telefonie.preview.error')}</span>}
    </span>
  );
}

/**
 * #1478 — Sprach-Modus-Umschalter (AK3): schreibt system_config.voice_provider
 * (cascade|s2s_openai) über PUT /api/einstellungen/voice_provider. Aktiver Modus
 * klar markiert (segmentierter Toggle, weiß+shadow). Initialwert aus settings-status.
 */
function ProviderModeCard({ initial }) {
  const { t } = useLanguage();
  const [provider, setProvider] = useState(initial === 's2s_openai' ? 's2s_openai' : 'cascade');
  const [saving, setSaving] = useState(null); // welcher Modus gerade gespeichert wird
  const [feedback, setFeedback] = useState(null); // {type, text}

  const switchTo = async (mode) => {
    if (mode === provider || saving) return;
    setSaving(mode); setFeedback(null);
    try {
      const r = await fetch(`${API}/api/einstellungen/voice_provider`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value: mode }),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      setProvider(mode);
      setFeedback({ type: 'ok', text: t('telefonie.voiceMode.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('telefonie.voiceMode.saveError') });
    } finally {
      setSaving(null);
    }
  };

  const OPTIONS = [
    { key: 'cascade', label: t('telefonie.voiceMode.cascade'), sub: t('telefonie.voiceMode.cascadeSub') },
    { key: 's2s_openai', label: t('telefonie.voiceMode.s2s'), sub: t('telefonie.voiceMode.s2sSub') },
  ];

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <ArrowRightLeft size={18} className="text-[#338edc]" />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.voiceMode.title')}</h2>
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.voiceMode.hint')}</p>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {OPTIONS.map((o) => {
            const active = provider === o.key;
            return (
              <button
                key={o.key}
                type="button"
                onClick={() => switchTo(o.key)}
                disabled={!!saving}
                className={`text-left rounded-xl border p-4 transition-colors disabled:opacity-60 ${active ? 'border-[#338edc] bg-[var(--bg-inset,#F8F9FC)] shadow-sm' : 'border-[var(--border-card,#E5E7EB)] hover:bg-[var(--bg-inset,#F8F9FC)]'}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--text-primary)]">{o.label}</span>
                  {saving === o.key
                    ? <Loader2 size={16} className="animate-spin text-[#338edc]" />
                    : active && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700"><CheckCircle2 size={12} /> {t('telefonie.voiceMode.active')}</span>}
                </div>
                <p className="text-xs text-[var(--text-tertiary)] mt-1">{o.sub}</p>
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-1">
          <span className="text-xs text-[var(--text-secondary)]">{t('telefonie.voiceMode.previewCascade')}</span>
          <VoicePreviewButton provider="cascade" label={t('telefonie.preview.listen')} />
          {feedback && <span className={`text-sm ${feedback.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{feedback.text}</span>}
        </div>
      </div>
    </div>
  );
}

// #1489 — Voice-Standardtexte editierbar (system_config via /api/einstellungen/:key).
// Greift Cascade + S2S: Begrüßungen laufen als openingText (beide Pfade sprechen ihn),
// der Aufnahme-Hinweis in der Webhook-TwiML vor dem Media-Stream (pfadunabhängig).
const VOICE_TEXT_FIELDS = [
  { key: 'voice_phone_greeting', labelKey: 'generic', hintKey: 'genericHint', rows: 2 },
  { key: 'voice_phone_greeting_personal', labelKey: 'personal', hintKey: 'personalHint', rows: 2, placeholderHint: true },
  { key: 'voice_phone_recording_notice', labelKey: 'recording', hintKey: 'recordingHint', rows: 3 },
];
const textareaCls = 'w-full px-3 py-2 text-sm border border-[var(--border-card,#E5E7EB)] rounded-lg bg-[var(--bg-card,#fff)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[#338edc]/30 resize-y';

function VoiceStandardtexteCard() {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState({});
  const [saved, setSaved] = useState({});
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'error', text}

  useEffect(() => {
    let alive = true;
    Promise.all(VOICE_TEXT_FIELDS.map((f) =>
      fetch(`${API}/api/einstellungen/${f.key}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => [f.key, typeof d?.value === 'string' ? d.value : ''])
        .catch(() => [f.key, ''])
    )).then((pairs) => {
      if (!alive) return;
      const o = Object.fromEntries(pairs);
      setVals(o); setSaved(o);
    }).catch(() => { if (alive) setFeedback({ type: 'error', text: t('telefonie.voiceTexts.loadError') }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [t]);

  const dirtyKeys = VOICE_TEXT_FIELDS.filter((f) => (vals[f.key] ?? '') !== (saved[f.key] ?? '')).map((f) => f.key);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      for (const key of dirtyKeys) {
        const r = await fetch(`${API}/api/einstellungen/${key}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ value: vals[key] ?? '' }),
        });
        if (!r.ok) throw new Error(`${r.status}`);
      }
      setSaved((prev) => ({ ...prev, ...Object.fromEntries(dirtyKeys.map((k) => [k, vals[k] ?? ''])) }));
      setFeedback({ type: 'ok', text: t('telefonie.voiceTexts.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('telefonie.voiceTexts.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <Volume2 size={18} className="text-[#338edc]" />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.voiceTexts.title')}</h2>
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.voiceTexts.hint')}</p>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>
        ) : (
          <div className="space-y-5">
            {VOICE_TEXT_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t(`telefonie.voiceTexts.${f.labelKey}`)}</label>
                <textarea
                  rows={f.rows}
                  value={vals[f.key] ?? ''}
                  onChange={(e) => { setVals((prev) => ({ ...prev, [f.key]: e.target.value })); setFeedback(null); }}
                  placeholder={t('telefonie.voiceTexts.emptyDefault')}
                  className={textareaCls}
                />
                <p className="text-xs text-[var(--text-muted)] mt-1">{t(`telefonie.voiceTexts.${f.hintKey}`)}</p>
                {f.placeholderHint && (
                  <p className="text-xs text-[var(--text-muted)] mt-0.5">{t('telefonie.voiceTexts.placeholderHint')}</p>
                )}
              </div>
            ))}
            <p className="text-xs text-[var(--text-muted)] border-t border-[var(--border-card,#E5E7EB)] pt-3">{t('telefonie.voiceTexts.afterHoursNote')}</p>
            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving || dirtyKeys.length === 0}
                className="h-9 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary, #338edc)' }}
              >
                {saving ? t('common.loading') : t('telefonie.voiceTexts.save')}
              </button>
              {feedback && (
                <span className={`text-sm ${feedback.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{feedback.text}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// #1797 (2.3/2.5) — „OpenAI Realtime": Modell (gpt-realtime | gpt-realtime-2 | -2.1),
// Reasoning-Aufwand (wird NUR an gpt-realtime-2* gesendet — ältere Modelle lehnen
// den Parameter hart ab) + Stimme (inkl. marin/cedar) in EINER Karte. Ersetzt die
// reine Stimmen-Karte (#1477); Keys gelten pro Anruf, kein Neustart nötig.
const S2S_MODELS = ['gpt-realtime', 'gpt-realtime-2', 'gpt-realtime-2.1'];
const S2S_EFFORTS = ['minimal', 'low', 'medium', 'high', 'xhigh'];

function S2sRealtimeSettingsCard() {
  const { t } = useLanguage();
  const KEYS = { model: 'voice_s2s_openai_model', effort: 'voice_s2s_reasoning_effort', voice: 'voice_s2s_openai_voice' };
  const DEFAULTS = { model: 'gpt-realtime', effort: 'low', voice: 'alloy' };
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState(DEFAULTS);
  const [saved, setSaved] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type:'ok'|'error', text}

  useEffect(() => {
    let alive = true;
    Promise.all(Object.entries(KEYS).map(([field, key]) =>
      fetch(`${API}/api/einstellungen/${key}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => [field, typeof d?.value === 'string' && d.value ? d.value : DEFAULTS[field]])
        .catch(() => [field, DEFAULTS[field]])
    )).then((pairs) => {
      if (!alive) return;
      const o = { ...DEFAULTS, ...Object.fromEntries(pairs) };
      setVals(o); setSaved(o);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirtyFields = Object.keys(KEYS).filter((f) => vals[f] !== saved[f]);
  const isRealtime2 = String(vals.model || '').startsWith('gpt-realtime-2');

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      for (const field of dirtyFields) {
        const r = await fetch(`${API}/api/einstellungen/${KEYS[field]}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ value: vals[field] }),
        });
        if (!r.ok) throw new Error(`${r.status}`);
      }
      setSaved({ ...vals });
      setFeedback({ type: 'ok', text: t('telefonie.s2sVoice.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('telefonie.s2sVoice.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <Bot size={18} className="text-[#22D3EE]" />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.s2sRealtime.title')}</h2>
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.s2sRealtime.hint')}</p>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.s2sRealtime.model')}</label>
                <select
                  value={vals.model}
                  onChange={(e) => { setVals((p) => ({ ...p, model: e.target.value })); setFeedback(null); }}
                  className={`${inputCls} w-full`}
                >
                  {S2S_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <p className="text-xs text-[var(--text-muted)] mt-1">{t('telefonie.s2sRealtime.modelHint')}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.s2sRealtime.effort')}</label>
                <select
                  value={vals.effort}
                  onChange={(e) => { setVals((p) => ({ ...p, effort: e.target.value })); setFeedback(null); }}
                  disabled={!isRealtime2}
                  className={`${inputCls} w-full disabled:opacity-50`}
                >
                  {S2S_EFFORTS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
                <p className="text-xs text-[var(--text-muted)] mt-1">{isRealtime2 ? t('telefonie.s2sRealtime.effortHint') : t('telefonie.s2sRealtime.effortOnly2')}</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.s2sVoice.label')}</label>
                <select
                  value={vals.voice}
                  onChange={(e) => { setVals((p) => ({ ...p, voice: e.target.value })); setFeedback(null); }}
                  className={`${inputCls} w-full`}
                >
                  {S2S_VOICES.map((v) => (
                    <option key={v} value={v}>{v}{(v === 'marin' || v === 'cedar') ? ` (${t('telefonie.s2sVoice.recommended')})` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* #1478 AK1 — gewählte (auch ungespeicherte) S2S-Stimme anhören */}
              <VoicePreviewButton provider="s2s_openai" voice={vals.voice} label={t('telefonie.preview.listen')} />
              <button
                onClick={save}
                disabled={saving || dirtyFields.length === 0}
                className="h-9 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary, #338edc)' }}
              >
                {saving ? t('common.loading') : t('telefonie.s2sVoice.save')}
              </button>
              {feedback && (
                <span className={`text-sm ${feedback.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{feedback.text}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// #1797 (2.1/2.2/2.5) — „Unterbrechung": Barge-in-Gate (Design A „Hold, dann Clear",
// nur s2s-Pfad) + OpenAI-native VAD-/Rauschunterdrückungs-Parameter. Gate-Default
// aus = exakt Bestandsverhalten; Events (bargein_*) werden immer geloggt (Baseline).
const BARGEIN_NUM_FIELDS = [
  { field: 'minSpeech', key: 'voice_bargein_min_speech_ms', min: 100, max: 2000, step: 50, labelKey: 'minSpeech', hintKey: 'minSpeechHint' },
  // #1797 (2b) — SNR-Gate im Hold-Fenster (Kalibrierung Call-207-Befund 20.08.)
  { field: 'snrDb', key: 'voice_bargein_snr_db', min: 3, max: 40, step: 1, labelKey: 'snrDb', hintKey: 'snrDbHint' },
  { field: 'minDbfs', key: 'voice_bargein_min_dbfs', min: -60, max: 0, step: 1, labelKey: 'minDbfs', hintKey: 'minDbfsHint' },
  { field: 'vadThreshold', key: 'voice_s2s_vad_threshold', min: 0.1, max: 1, step: 0.05, labelKey: 'vadThreshold', hintKey: 'vadThresholdHint', serverVadOnly: true },
  { field: 'vadSilence', key: 'voice_s2s_vad_silence_ms', min: 100, max: 3000, step: 50, labelKey: 'vadSilence', hintKey: 'vadSilenceHint', serverVadOnly: true },
  { field: 'vadPrefix', key: 'voice_s2s_vad_prefix_ms', min: 0, max: 2000, step: 50, labelKey: 'vadPrefix', hintKey: 'vadPrefixHint', serverVadOnly: true },
];

function BargeInSettingsCard() {
  const { t } = useLanguage();
  const KEYS = {
    gate: 'voice_bargein_gate_enabled',
    minSpeech: 'voice_bargein_min_speech_ms',
    snrDb: 'voice_bargein_snr_db',
    minDbfs: 'voice_bargein_min_dbfs',
    backchannels: 'voice_bargein_backchannels',
    turnDetection: 'voice_s2s_turn_detection',
    eagerness: 'voice_s2s_vad_eagerness',
    vadThreshold: 'voice_s2s_vad_threshold',
    vadSilence: 'voice_s2s_vad_silence_ms',
    vadPrefix: 'voice_s2s_vad_prefix_ms',
    noiseReduction: 'voice_s2s_noise_reduction',
  };
  const DEFAULTS = { gate: false, minSpeech: '300', snrDb: '12', minDbfs: '-28', backchannels: '', turnDetection: 'server_vad', eagerness: 'auto', vadThreshold: '0.6', vadSilence: '500', vadPrefix: '300', noiseReduction: 'near_field' };
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState(DEFAULTS);
  const [saved, setSaved] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all(Object.entries(KEYS).map(([field, key]) =>
      fetch(`${API}/api/einstellungen/${key}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const v = d?.value;
          if (field === 'gate') return [field, v === true || v === 'true'];
          if (field === 'backchannels') return [field, Array.isArray(v) ? v.join(', ') : ''];
          return [field, (v === null || v === undefined || v === '') ? DEFAULTS[field] : String(v)];
        })
        .catch(() => [field, DEFAULTS[field]])
    )).then((pairs) => {
      if (!alive) return;
      const o = { ...DEFAULTS, ...Object.fromEntries(pairs) };
      setVals(o); setSaved(o);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirtyFields = Object.keys(KEYS).filter((f) => vals[f] !== saved[f]);
  const semantic = vals.turnDetection === 'semantic_vad';

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      for (const field of dirtyFields) {
        let value = vals[field];
        if (field === 'backchannels') value = String(value).split(',').map((w) => w.trim().toLowerCase()).filter(Boolean);
        if (['minSpeech', 'snrDb', 'minDbfs', 'vadThreshold', 'vadSilence', 'vadPrefix'].includes(field)) value = Number(value);
        const r = await fetch(`${API}/api/einstellungen/${KEYS[field]}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ value }),
        });
        if (!r.ok) throw new Error(`${r.status}`);
      }
      setSaved({ ...vals });
      setFeedback({ type: 'ok', text: t('telefonie.bargein.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('telefonie.bargein.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <Zap size={18} className="text-[#F59E0B]" />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.bargein.title')}</h2>
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.bargein.hint')}</p>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>
        ) : (
          <div className="space-y-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={vals.gate}
                onChange={(e) => { setVals((p) => ({ ...p, gate: e.target.checked })); setFeedback(null); }}
                className="mt-0.5 h-4 w-4 rounded border-[var(--border-card,#E5E7EB)] accent-[#338edc]"
              />
              <span>
                <span className="block text-sm font-medium text-[var(--text-primary)]">{t('telefonie.bargein.gateLabel')}</span>
                <span className="block text-xs text-[var(--text-muted)] mt-0.5">{t('telefonie.bargein.gateHint')}</span>
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {BARGEIN_NUM_FIELDS.map((f) => (
                <div key={f.field}>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t(`telefonie.bargein.${f.labelKey}`)}</label>
                  <input
                    type="number"
                    min={f.min} max={f.max} step={f.step}
                    value={vals[f.field]}
                    disabled={f.serverVadOnly && semantic}
                    onChange={(e) => { setVals((p) => ({ ...p, [f.field]: e.target.value })); setFeedback(null); }}
                    className={`${inputCls} w-full disabled:opacity-50`}
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">{t(`telefonie.bargein.${f.hintKey}`)}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.bargein.turnDetection')}</label>
                <select
                  value={vals.turnDetection}
                  onChange={(e) => { setVals((p) => ({ ...p, turnDetection: e.target.value })); setFeedback(null); }}
                  className={`${inputCls} w-full`}
                >
                  <option value="server_vad">{t('telefonie.bargein.tdServerVad')}</option>
                  <option value="semantic_vad">{t('telefonie.bargein.tdSemanticVad')}</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.bargein.eagerness')}</label>
                <select
                  value={vals.eagerness}
                  disabled={!semantic}
                  onChange={(e) => { setVals((p) => ({ ...p, eagerness: e.target.value })); setFeedback(null); }}
                  className={`${inputCls} w-full disabled:opacity-50`}
                >
                  {['auto', 'low', 'medium', 'high'].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.bargein.noiseReduction')}</label>
                <select
                  value={vals.noiseReduction}
                  onChange={(e) => { setVals((p) => ({ ...p, noiseReduction: e.target.value })); setFeedback(null); }}
                  className={`${inputCls} w-full`}
                >
                  <option value="near_field">{t('telefonie.bargein.nrNearField')}</option>
                  <option value="far_field">{t('telefonie.bargein.nrFarField')}</option>
                  <option value="off">{t('telefonie.bargein.nrOff')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.bargein.backchannels')}</label>
              <input
                type="text"
                value={vals.backchannels}
                onChange={(e) => { setVals((p) => ({ ...p, backchannels: e.target.value })); setFeedback(null); }}
                placeholder="ja, mhm, okay, genau, …"
                className={`${inputCls} w-full`}
              />
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('telefonie.bargein.backchannelsHint')}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving || dirtyFields.length === 0}
                className="h-9 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary, #338edc)' }}
              >
                {saving ? t('common.loading') : t('telefonie.bargein.save')}
              </button>
              {feedback && (
                <span className={`text-sm ${feedback.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{feedback.text}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// #1804 — A-Kunden-Eskalation: Master-Switch + Empfängerliste (mit erkanntem
// Medium je User: Telegram/SMS/kein Kanal) + Nur-Eingestempelt-Toggle + read-only
// Anzeige der A-Kunden-Quelle (kunden.segment, Schwelle aus segment_config).
function EskalationSettingsCard() {
  const { t } = useLanguage();
  const KEYS = {
    enabled: 'voice_eskalation_enabled',
    nurEingestempelt: 'voice_eskalation_nur_eingestempelt',
    recipients: 'voice_eskalation_recipients',
    // #1806 — Eskalationsleiter: Direktverbindung + Timeout
    direktverbindung: 'voice_leiter_direktverbindung',
    timeoutSek: 'voice_leiter_timeout_sek',
  };
  const DEFAULTS = { enabled: false, nurEingestempelt: true, recipients: [], direktverbindung: true, timeoutSek: 25 };
  const [loading, setLoading] = useState(true);
  const [vals, setVals] = useState(DEFAULTS);
  const [saved, setSaved] = useState(DEFAULTS);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [userStatus, setUserStatus] = useState({ users: [], quelle: null });
  const [pick, setPick] = useState('');

  useEffect(() => {
    let alive = true;
    const cfg = Promise.all(Object.entries(KEYS).map(([field, key]) =>
      fetch(`${API}/api/einstellungen/${key}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          const v = d?.value;
          if (field === 'recipients') return [field, Array.isArray(v) ? v.map(Number).filter(Number.isInteger) : []];
          if (field === 'timeoutSek') return [field, Number(String(v ?? '').replace(/"/g, '')) || DEFAULTS.timeoutSek];
          return [field, v === null || v === undefined ? DEFAULTS[field] : (v === true || v === 'true')];
        })
        .catch(() => [field, DEFAULTS[field]])
    ));
    const st = fetch(`${API}/api/voip/eskalation/empfaenger-status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { users: [], quelle: null }))
      .catch(() => ({ users: [], quelle: null }));
    Promise.all([cfg, st]).then(([pairs, s]) => {
      if (!alive) return;
      const o = { ...DEFAULTS, ...Object.fromEntries(pairs) };
      setVals(o); setSaved(o); setUserStatus(s || { users: [], quelle: null });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = vals.enabled !== saved.enabled
    || vals.nurEingestempelt !== saved.nurEingestempelt
    || vals.direktverbindung !== saved.direktverbindung
    || vals.timeoutSek !== saved.timeoutSek
    || JSON.stringify(vals.recipients) !== JSON.stringify(saved.recipients);
  const byId = useMemo(() => new Map((userStatus.users || []).map((u) => [u.id, u])), [userStatus]);
  const selectable = (userStatus.users || []).filter((u) => !vals.recipients.includes(u.id));

  const kanalBadge = (kanal) => {
    if (kanal === 'telegram') return <span className="px-1.5 py-0.5 text-[11px] font-medium rounded-full bg-green-100 text-green-700">Telegram</span>;
    if (kanal === 'sms') return <span className="px-1.5 py-0.5 text-[11px] font-medium rounded-full bg-blue-100 text-blue-700">SMS</span>;
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium rounded-full bg-amber-100 text-amber-700">
        <AlertTriangle size={10} /> {t('telefonie.eskalation.keinKanal')}
      </span>
    );
  };

  const save = async () => {
    setSaving(true); setFeedback(null);
    try {
      for (const field of Object.keys(KEYS)) {
        const changed = field === 'recipients'
          ? JSON.stringify(vals.recipients) !== JSON.stringify(saved.recipients)
          : vals[field] !== saved[field];
        if (!changed) continue;
        // #1806 — Timeout hart auf 10–45 s geklemmt (Server klemmt zusätzlich).
        const value = field === 'timeoutSek'
          ? Math.min(Math.max(Math.round(Number(vals.timeoutSek) || 25), 10), 45)
          : vals[field];
        const r = await fetch(`${API}/api/einstellungen/${KEYS[field]}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ value }),
        });
        if (!r.ok) throw new Error(`${r.status}`);
      }
      setSaved({ ...vals, recipients: [...vals.recipients] });
      setFeedback({ type: 'ok', text: t('telefonie.eskalation.saved') });
    } catch {
      setFeedback({ type: 'error', text: t('telefonie.eskalation.saveError') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
        <div className="flex items-center gap-2">
          <AlertTriangle size={18} className="text-[#DC2626]" />
          <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.eskalation.title')}</h2>
        </div>
        <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.eskalation.hint')}</p>
      </div>
      <div className="p-5">
        {loading ? (
          <div className="text-sm text-[var(--text-tertiary)]">{t('common.loading')}</div>
        ) : (
          <div className="space-y-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={vals.enabled}
                onChange={(e) => { setVals((p) => ({ ...p, enabled: e.target.checked })); setFeedback(null); }}
                className="mt-0.5 h-4 w-4 rounded border-[var(--border-card,#E5E7EB)] accent-[#338edc]"
              />
              <span>
                <span className="block text-sm font-medium text-[var(--text-primary)]">{t('telefonie.eskalation.enabledLabel')}</span>
                <span className="block text-xs text-[var(--text-muted)] mt-0.5">{t('telefonie.eskalation.enabledHint')}</span>
              </span>
            </label>

            <div>
              <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.eskalation.recipientsLabel')}</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {vals.recipients.length === 0 && (
                  <span className="text-xs text-[var(--text-muted)]">{t('telefonie.eskalation.noRecipients')}</span>
                )}
                {vals.recipients.map((id) => {
                  const u = byId.get(id);
                  return (
                    <span key={id} className="inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1 text-sm rounded-lg border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-primary)]">
                      {u ? u.name : `User #${id}`}
                      {kanalBadge(u ? u.kanal : 'kein_kanal')}
                      <button
                        onClick={() => { setVals((p) => ({ ...p, recipients: p.recipients.filter((x) => x !== id) })); setFeedback(null); }}
                        className="text-[var(--text-muted)] hover:text-red-600"
                        title={t('common.delete')}
                      >
                        <Trash2 size={13} />
                      </button>
                    </span>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <select value={pick} onChange={(e) => setPick(e.target.value)} className={`${inputCls}`}>
                  <option value="">{t('telefonie.eskalation.pickUser')}</option>
                  {selectable.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}{u.kanal === 'telegram' ? ' · Telegram' : u.kanal === 'sms' ? ' · SMS' : ` · ${t('telefonie.eskalation.keinKanal')}`}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    const id = Number(pick);
                    if (!id) return;
                    setVals((p) => ({ ...p, recipients: [...p.recipients, id] }));
                    setPick(''); setFeedback(null);
                  }}
                  disabled={!pick}
                  className="h-9 px-3 rounded-lg text-sm font-medium border border-[var(--border-card,#E5E7EB)] text-[var(--text-primary)] disabled:opacity-50 inline-flex items-center gap-1"
                >
                  <Plus size={14} /> {t('telefonie.eskalation.addUser')}
                </button>
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">{t('telefonie.eskalation.recipientsHint')}</p>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={vals.nurEingestempelt}
                onChange={(e) => { setVals((p) => ({ ...p, nurEingestempelt: e.target.checked })); setFeedback(null); }}
                className="mt-0.5 h-4 w-4 rounded border-[var(--border-card,#E5E7EB)] accent-[#338edc]"
              />
              <span>
                <span className="block text-sm font-medium text-[var(--text-primary)]">{t('telefonie.eskalation.nurEingestempeltLabel')}</span>
                <span className="block text-xs text-[var(--text-muted)] mt-0.5">{t('telefonie.eskalation.nurEingestempeltHint')}</span>
              </span>
            </label>

            {/* #1806 — Eskalationsleiter: Direktverbindung + Timeout */}
            <div className="border-t border-[var(--border-card,#E5E7EB)] pt-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={vals.direktverbindung}
                  onChange={(e) => { setVals((p) => ({ ...p, direktverbindung: e.target.checked })); setFeedback(null); }}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--border-card,#E5E7EB)] accent-[#338edc]"
                />
                <span>
                  <span className="block text-sm font-medium text-[var(--text-primary)]">{t('telefonie.leiter.direktLabel')}</span>
                  <span className="block text-xs text-[var(--text-muted)] mt-0.5">{t('telefonie.leiter.direktHint')}</span>
                </span>
              </label>
              <div className="flex items-center gap-3">
                <label className="text-xs font-medium text-[var(--text-secondary)]">{t('telefonie.leiter.timeoutLabel')}</label>
                <input
                  type="number"
                  min={10}
                  max={45}
                  value={vals.timeoutSek}
                  onChange={(e) => { setVals((p) => ({ ...p, timeoutSek: Number(e.target.value) })); setFeedback(null); }}
                  disabled={!vals.direktverbindung}
                  className={`${inputCls} w-24`}
                />
                <span className="text-xs text-[var(--text-muted)]">{t('telefonie.leiter.timeoutHint')}</span>
              </div>
            </div>

            <div className="border border-[var(--border-card,#E5E7EB)] rounded-lg p-3 bg-[var(--bg-inset,#F8F9FC)]">
              <p className="text-xs font-medium text-[var(--text-secondary)] mb-1">{t('telefonie.eskalation.quelleTitle')}</p>
              <p className="text-xs text-[var(--text-muted)]">
                {t('telefonie.eskalation.quelleText')}{' '}
                <code className="text-[11px]">{userStatus.quelle?.feld || 'kunden.segment'}</code>
                {userStatus.quelle?.a_schwelle_pakete_monat
                  ? ` — ${t('telefonie.eskalation.quelleSchwelle', { n: userStatus.quelle.a_schwelle_pakete_monat })}`
                  : ''}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={save}
                disabled={saving || !dirty}
                className="h-9 px-4 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ background: 'var(--primary, #338edc)' }}
              >
                {saving ? t('common.loading') : t('telefonie.eskalation.save')}
              </button>
              {feedback && (
                <span className={`text-sm ${feedback.type === 'ok' ? 'text-green-600' : 'text-red-600'}`}>{feedback.text}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function TelefonieEinstellungenPage() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/voip/settings-status`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setStatus)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const featureLabel = (key) => t(`telefonie.feature.${key}`);

  return (
    <div className="p-6 space-y-6">
      <PageHead title={t('telefonie.einstellungen.title')} subtitle={t('telefonie.einstellungen.subtitle')} />

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)] py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> {t('common.loading')}
        </div>
      )}

      {error && (
        <div className="border rounded-xl p-4" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">{t('telefonie.einstellungen.loadError')}: {error}</span>
          </div>
        </div>
      )}

      {status && (
        <>
          {/* Verbindung */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <Phone size={20} className="text-[#338edc]" />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.hotlineNumber')}</p>
              </div>
              {/* #1478 — Voice-/Hotline-Nummer (eigene Twilio-Nummer), NICHT der SMS-Sender */}
              <p className="text-2xl font-bold text-[var(--text-primary)]">{status.hotline_number || '—'}</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">{status.hotline_number ? t('telefonie.einstellungen.numberConfigured') : t('telefonie.einstellungen.numberMissing')}</p>
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <Webhook size={20} className="text-[#6366F1]" />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.webhookTarget')}</p>
              </div>
              <p className="text-sm font-semibold text-[var(--text-primary)] break-all mt-2">{status.webhook_url}</p>
            </div>
            <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <KeyRound size={20} className="text-[#10B981]" />
                <p className="text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.credentials')}</p>
              </div>
              <div className="mt-2 space-y-1.5">
                {Object.entries(status.secrets_configured || {}).map(([key, ok]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-[var(--text-secondary)]">{t(`telefonie.secret.${key}`)}</span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${ok ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {ok ? t('telefonie.einstellungen.configured') : t('telefonie.einstellungen.notConfigured')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Feature-Status */}
          <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--border-card,#E5E7EB)]">
              <h2 className="text-base font-semibold text-[var(--text-primary)]">{t('telefonie.einstellungen.featuresTitle')}</h2>
              <p className="text-sm text-[var(--text-tertiary)] mt-0.5">{t('telefonie.einstellungen.featuresHint')}</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--bg-inset,#F8F9FC)]">
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.colFeature')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.colKey')}</th>
                  <th className="px-4 py-3 text-left text-xs uppercase tracking-wider font-medium text-[var(--text-secondary)]">{t('telefonie.einstellungen.colStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-table,#F3F4F6)]">
                {(status.features || []).map((f) => (
                  <tr key={f.key} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors">
                    <td className="px-4 py-3 text-[var(--text-primary)]">{featureLabel(f.key)}</td>
                    <td className="px-4 py-3"><code className="text-xs text-[var(--text-tertiary)]">{f.key}</code></td>
                    <td className="px-4 py-3">
                      {f.enabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          <CheckCircle2 size={12} /> {t('telefonie.einstellungen.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                          <MoonStar size={12} /> {t('telefonie.einstellungen.dormant')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* #1478 Sprach-Modus-Umschalter (Kaskade ⇄ S2S OpenAI) + Cascade-Vorschau */}
          <ProviderModeCard initial={status.voice_provider} />

          {/* #1477/#1797 OpenAI Realtime: Modell + Reasoning + Stimme (+ Anhören #1478) */}
          <S2sRealtimeSettingsCard />

          {/* #1797 Unterbrechung: Barge-in-Gate (Design A) + native VAD-/NR-Parameter */}
          <BargeInSettingsCard />

          {/* #1804 A-Kunden-Eskalation: Master-Switch + Empfänger (Medium-Anzeige) */}
          <EskalationSettingsCard />

          {/* #1489 Voice-Standardtexte (Begrüßung/personalisiert/Aufnahme-Hinweis) editierbar */}
          <VoiceStandardtexteCard />

          {/* #1432 Gruppen/Teams */}
          <GruppenSection />

          {/* #1433 Durchwahlen */}
          <DurchwahlenSection />

          {/* #1443 Blocklist (Gate 4) */}
          <BlocklistSection />

          {/* #1485 Terminbuchungen-Konfiguration (Stufe A) */}
          <TerminbuchungenSection />
        </>
      )}
    </div>
  );
}
